// api.js — proc-v3 Fornitori (F2.3c): modulo PURO (niente JSX, niente DOM,
// niente side-effect) — win arriva SEMPRE per parametro. È QUESTO che il
// contract test server/src/lib/proc-v3-suppliers.test.js esercita sotto node.
//
// SORGENTE DATI: lo store del MONOLITE (window.store / window.DB, globali
// dichiarati in proc-v0.js:1441/1279). Qui non si legge il server direttamente:
// il v3 possiede SOLO la resa della lista, i dati e le scritture restano v1.
//
// LABEL: copiate VERBATIM dal dizionario I18N di proc-v0.js (const lessicale,
// NON raggiungibile da fuori — proc-v0.js:1230). Il test di sincronizzazione
// RILEGGE proc-v0.js e fallisce al primo drift. Lingua: crm_ui_lang || 'it',
// stessa chiave del v1 (proc-v0.js:18).

export const LABELS = {
  en: {
    'sup.title': 'Suppliers',
    'sup.new': '+ New supplier',
    'sup.bulk_import': 'Bulk import',
    'sup.any_confidence': 'Any confidence',
    'sup.confidence.high': 'High',
    'sup.confidence.medium': 'Medium',
    'sup.confidence.low': 'Low',
    'sup.qualified_filter': 'Qualified',
    'sup.prospect_filter': 'Prospects',
    'sup.all_filter': 'All',
    'sup.sleeping': 'Sleeping only (≥14d quiet)',
    'col.name': 'Name',
    'col.country': 'Country',
    'col.status': 'Status',
    'col.confidence': 'Affidabilità',
    'col.categories': 'Categories',
    'col.tags': 'Tags',
    'col.contacts': 'Contacts',
    'badge.high': 'High',
    'badge.medium': 'Medium',
    'badge.low': 'Low',
  },
  it: {
    'sup.title': 'Fornitori',
    'sup.new': '+ Nuovo fornitore',
    'sup.bulk_import': 'Importa lista',
    'sup.any_confidence': 'Qualsiasi affidabilità',
    'sup.confidence.high': 'Alta',
    'sup.confidence.medium': 'Media',
    'sup.confidence.low': 'Bassa',
    'sup.qualified_filter': 'Qualificati',
    'sup.prospect_filter': 'Prospect',
    'sup.all_filter': 'Tutti',
    'sup.sleeping': 'Solo dormienti (≥14g silenzio)',
    'col.name': 'Nome',
    'col.country': 'Paese',
    'col.status': 'Stato',
    'col.confidence': 'Affidabilità',
    'col.categories': 'Categorie',
    'col.tags': 'Tag',
    'col.contacts': 'Contatti',
    'badge.high': 'Alta',
    'badge.medium': 'Media',
    'badge.low': 'Bassa',
  },
};

// Testi LETTERALI del v1 (fuori dal dizionario I18N: in proc-v0.js sono
// hardcoded in renderSuppliers/makeDataTable) — stessi byte, pinnati dal test.
export const TEXTS = {
  subtitle: 'I concorrenti sono esclusi — vedi la vista Concorrenti per le quotazioni di riferimento di mercato.',
  addedCol: 'Aggiunto',
  emptyMessage: 'Nessun fornitore corrisponde ai filtri.',
  anyCategory: 'Qualsiasi categoria',
  anyCategoryPlaceholder: 'Qualsiasi categoria — scrivi per cercare',
  bulkAddCategory: 'Aggiungi a categoria…',
  bulkAddTag: 'Aggiungi tag…',
  bulkQualify: 'Qualifica',
  bulkProspect: 'Prospect',
  bulkEdit: 'Modifica in blocco…',
  bulkDelete: 'Elimina',
};

export function pickLang(win) {
  try {
    return (win.localStorage.getItem('crm_ui_lang') || 'it') === 'en' ? 'en' : 'it';
  } catch (e) {
    return 'it';
  }
}

export function tLabel(win, key) {
  const lang = pickLang(win);
  const d = LABELS[lang] || LABELS.it;
  return d[key] != null ? d[key] : (LABELS.it[key] != null ? LABELS.it[key] : key);
}

// Stato dei filtri alti — DEFAULT identici a supFilters del v1 (proc-v0.js:6398).
// NB: `search` esiste nel v1 ma la vista non ha un campo che lo valorizzi
// (la ricerca libera vive nei filtri per colonna della tabella): resta ''.
export function defaultFilters() {
  return { search: '', qualified: 'all', confidence: 'all', category: 'all', sleeping: false };
}

// Lettura dati: STESSA composizione di renderSuppliers v1 (proc-v0.js:6537-6603):
// listSuppliers con gli stessi argomenti (excludeKind:'competitor' compreso),
// mappa dormienti = max daysQuiet per fornitore da listSleepingCS(14), conteggio
// contatti da DB.contacts, pre-sort createdAt desc, stesse colonne derivate.
// NB filtro confidence (o17): le option del select portano il value CANONICO
// 'High'/'Medium'/'Low' — bug v1:6581 CORRETTO in entrambi i mondi (prima
// l'option senza value inviava il testo tradotto e in it non matchava mai
// listSuppliers:1564). readSuppliers passa il valore COSÌ COM'È allo store.
export function readSuppliers(win, filters) {
  const store = win.store;
  const f = filters || defaultFilters();
  let list = store.listSuppliers({
    qualified: f.qualified === 'all' ? undefined : f.qualified === 'qualified',
    confidence: f.confidence === 'all' ? undefined : f.confidence,
    category: f.category === 'all' ? undefined : f.category,
    search: f.search || undefined,
    excludeKind: 'competitor',
  });
  const sleepingByDays = {};
  store.listSleepingCS(14).forEach((x) => {
    if (!sleepingByDays[x.supplier.id] || x.daysQuiet > sleepingByDays[x.supplier.id]) {
      sleepingByDays[x.supplier.id] = x.daysQuiet;
    }
  });
  if (f.sleeping) list = list.filter((s) => sleepingByDays[s.id] !== undefined);
  const contacts = (win.DB && win.DB.contacts) || [];
  // Ordinamento di default: per data di aggiunta, più recenti in alto (v1:6591)
  list = list.slice().sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  // PARITÀ ESATTA dell'ordine iniziale (provata dal collaudo, righe divergenti
  // alla pos. 11 senza questo): makeDataTable v1 applica il defaultSort come
  // sort ASC STABILE + reverse() (proc-v0.js:10866-10874) — sui PARI MERITO
  // createdAt l'ordine esce INVERTITO rispetto al pre-sort. Si replica qui:
  // così il sort stabile del core (desc, ties = ordine d'arrivo) conserva
  // l'ordine v1 byte-per-byte. Deviazione residua SOLO sui sort da click
  // utente (dichiarata, deviazione 1 dello studio f23c).
  const ts = (s) => (s.createdAt ? new Date(s.createdAt).getTime() : 0);
  list = list
    .map((s, i) => ({ s, i }))
    .sort((a, b) => (ts(a.s) - ts(b.s)) || (a.i - b.i))
    .map((x) => x.s)
    .reverse();
  const rows = list.map((s) => ({
    ...s,
    _country: s.country || '',
    _statusText: s.qualified ? 'qualified' : 'prospect',
    _confText: s.confidence || '',
    _catsText: (s.categories || []).join(', '),
    _tagsText: (s.tags || []).join(', '),
    _contactsCount: contacts.filter((c) => c.supplierId === s.id).length,
    // undefined = NON dormiente; null = dormiente senza data valida (badge senza numero)
    _sleepingDays: sleepingByDays[s.id],
  }));
  return { rows, cats: store.listCategories() };
}

// prettyDate del v1 (proc-v0.js:3144) troncata alla sola data, come la colonna
// "Aggiunto" (v1:6626 usa prettyDate(r.createdAt).slice(0,10)).
export function fmtCreated(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }).slice(0, 10);
}

// confidenceBadge v1 (proc-v0.js:3172): colore + etichetta tradotta.
export function confColor(c) {
  return c === 'High' ? 'text-emerald-700' : c === 'Medium' ? 'text-amber-700' : 'text-slate-500';
}
export function confLabel(win, c) {
  const key = c === 'High' ? 'badge.high' : c === 'Medium' ? 'badge.medium' : c === 'Low' ? 'badge.low' : null;
  return key ? tLabel(win, key) : (c || '');
}

// tagBadge v1 (proc-v0.js:3160): scope "body @ scope" + mappa colori.
export function tagParts(tag) {
  const isScoped = String(tag).includes(' @ ');
  const [body, scope] = isScoped ? String(tag).split(' @ ') : [String(tag), null];
  let color = 'bg-rose-100 text-rose-800';
  if (body === 'type:competitor') color = 'bg-fuchsia-100 text-fuchsia-800';
  else if (body.startsWith('type:')) color = 'bg-purple-100 text-purple-800';
  else if (body.startsWith('brand:')) color = 'bg-cyan-100 text-cyan-800';
  else if (body.startsWith('size:') || body.startsWith('payment:')) color = 'bg-emerald-100 text-emerald-800';
  return { body, scope, color };
}

// --- Testi di match dei filtri per colonna -----------------------------------
// Il v1 filtra su stripHtml(render(row)) (makeDataTable, proc-v0.js:10856):
// qui si riproduce ESATTAMENTE quel testo (concatenazioni comprese) per ogni
// colonna con render custom. Funzioni pure, testate.

export function nameFilterText(r) {
  // v1: `<span>Nome</span><span>sleeping 21d</span>` → strip = 'Nomesleeping 21d'
  return String(r.name || '') + (r._sleepingDays !== undefined
    ? 'sleeping' + (Number.isFinite(r._sleepingDays) ? ' ' + r._sleepingDays + 'd' : '')
    : '');
}

export function statusFilterText(r) {
  return r.qualified ? 'qualified' : 'prospect'; // il v1 renderizza i letterali inglesi in entrambe le lingue
}

// Celle categorie: prime 2 + '+N' (v1:6623). text = ciò che vede il filtro v1.
export function catsCellData(r) {
  const all = r.categories || [];
  const shown = all.slice(0, 2);
  const more = all.length > 2 ? all.length - 2 : 0;
  return { shown, more, text: shown.join(' ') + (more ? '+' + more : '') };
}

// Celle tag: primi 3 come badge + '+N' (v1:6624). text replica lo strip dei badge.
export function tagsCellData(r) {
  const all = r.tags || [];
  const shown = all.slice(0, 3).map(tagParts);
  const more = all.length > 3 ? all.length - 3 : 0;
  const text = shown.map((p) => p.body + (p.scope ? ' @ ' + p.scope : '')).join(' ') + (more ? '+' + more : '');
  return { shown, more, text };
}

// --- Deleghe al v1 (il v3 possiede SOLO la resa della lista) ------------------
// Tutte function declaration top-level del monolite ⇒ globali su window.

export function openNewSupplier(win) { win.openSupplierModal(null); }
export function openBulkImport(win) { win.openBulkImportSuppliersModal(); }
export function openSupplierDetail(win, id) {
  win.currentSupplierId = id;           // pattern v1 (proc-v0.js:6629)
  win.showView('supplier-detail');      // il dettaglio RESTA v1 (confine dichiarato)
}

// Risoluzione selezione: include le righe selezionate ma fuori dal filtro
// corrente (parità selectionResolve v1:6609).
export function resolveSelection(win, ids) {
  return ids.map((id) => win.store.getSupplier(id)).filter(Boolean);
}

// Azioni bulk: STESSO codice v1 (payload identici per costruzione: confirmWrite/
// CAS/toast del monolite). L'ordine e le etichette sono quelli di v1:6610-6617.
export function buildBulkActions(win) {
  return [
    { label: TEXTS.bulkAddCategory, fn: (sel) => win._bulkSupAddCategory(sel) },
    { label: TEXTS.bulkAddTag, fn: (sel) => win._bulkSupTag(sel) },
    { label: TEXTS.bulkQualify, fn: (sel) => win._bulkSupQualify(sel, true) },
    { label: TEXTS.bulkProspect, fn: (sel) => win._bulkSupQualify(sel, false) },
    { label: TEXTS.bulkEdit, fn: (sel) => win._bulkSupEdit(sel) },
    { label: TEXTS.bulkDelete, fn: (sel) => win._bulkSupDelete(sel) },
  ];
}

// Globali del monolite che il v3 DELEGA (guardie dell'install + test statico:
// se un rename li toglie, l'install resta inerte e il contract test va ROSSO).
export const DELEGATED_GLOBALS = [
  'showView', 'openSupplierModal', 'openBulkImportSuppliersModal',
  '_bulkSupAddCategory', '_bulkSupTag', '_bulkSupQualify', '_bulkSupEdit', '_bulkSupDelete',
  'makeSearchableSelect', '_categoryOption',
];

// ============================================================================
// F2.3d — rank di ordinamento PER-COLONNA che replicano ESATTAMENTE il
// click-sort del makeDataTable v1 (proc-v0.js:10862-10875)
// ============================================================================
// Il v1 ordina PARTENDO DALL'ORDINE BASE della vista (rows come composte dal
// render), con sort ASC STABILE (comparatore: type 'number' → Number()||0,
// type 'date' → getTime, altrimenti String(..).toLowerCase e confronto `<` a
// code-unit — NON localeCompare) e per il desc fa asc + reverse(): i PARI
// MERITO escono in ordine base per l'asc e INVERTITI per il desc.
// Qui si assegna a ogni riga un RANK UNIVOCO per colonna (posizione nell'asc
// v1) usato come sortValue NUMERICO nel DataTable: asc = ordine v1 identico;
// desc = reverse esatto (con rank univoci, desc stabile ≡ asc+reverse).
// Così anche i pari merito da click utente restano byte-identici al v1 —
// chiusa per queste viste la deviazione 1 dichiarata dallo studio f23c.
export function assignV1SortRanks(rows, cols) {
  for (const c of cols) {
    const val = (r) => {
      let v = typeof c.sortKey === 'function' ? c.sortKey(r) : r[c.key];
      if (c.type === 'number') v = Number(v) || 0;
      else if (c.type === 'date') v = v ? new Date(v).getTime() : 0;
      else v = String(v ?? '').toLowerCase();
      return v;
    };
    rows
      .map((r, i) => ({ r, i, v: val(r) }))
      .sort((a, b) => (a.v < b.v ? -1 : a.v > b.v ? 1 : a.i - b.i))
      .forEach((x, pos) => {
        if (!x.r._v1Rank) x.r._v1Rank = {};
        x.r._v1Rank[c.key] = pos;
      });
  }
  return rows;
}

// ============================================================================
// F2.3d — vista "Prodotti" (renderProducts v1, proc-v0.js:7675-7775)
// ============================================================================

// LABEL dal dizionario I18N v1 (VERBATIM, test di sync come per suppliers).
export const PROD_LABELS = {
  en: {
    'prod.title': 'Products',
    'prod.subtitle': 'Type words in any column header — multi-word AND search with highlighting.',
    'prod.new': '+ New product',
    'col.name': 'Name',
    'col.category': 'Category',
    'col.brand': 'Brand',
    'col.suppliers_offering': 'Suppliers offering',
    'col.schema': 'Schema',
  },
  it: {
    'prod.title': 'Prodotti',
    'prod.subtitle': 'Digita parole nell\'header di qualsiasi colonna — ricerca multi-parola AND con evidenziazione.',
    'prod.new': '+ Nuovo prodotto',
    'col.name': 'Nome',
    'col.category': 'Categoria',
    'col.brand': 'Marca',
    'col.suppliers_offering': 'Fornitori che lo offrono',
    'col.schema': 'Schema',
  },
};

// Testi LETTERALI del v1 (hardcoded in renderProducts, fuori dal dict I18N).
export const PROD_TEXTS = {
  showPhotos: 'Mostra foto',
  photoSizeLabel: 'Dimensione',
  photoCol: 'Foto',
  lastQuoteCol: 'Ult. quotazione',
  emptyMessage: 'Nessun prodotto presente.',
  bulkMoveCategory: 'Sposta in categoria…',
  bulkDelete: 'Elimina',
};

export function tProd(win, key) {
  const lang = pickLang(win);
  const d = PROD_LABELS[lang] || PROD_LABELS.it;
  return d[key] != null ? d[key] : (PROD_LABELS.it[key] != null ? PROD_LABELS.it[key] : key);
}

// Preferenze foto del v1 (STESSE chiavi localStorage: v1↔v3 si scambiano le
// preferenze senza attriti). Default e clamp identici a v1:7679-7682.
export function readProductPrefs(win) {
  let showPhotos = true;
  try { showPhotos = (win.localStorage.getItem('crm_prod_show_photos') || '1') !== '0'; } catch (e) { /* default v1 */ }
  let photoSize = 44;
  try { photoSize = Math.min(140, Math.max(28, parseInt(win.localStorage.getItem('crm_prod_photo_size') || '44', 10) || 44)); } catch (e) { /* default v1 */ }
  return { showPhotos, photoSize };
}
export function writeShowPhotos(win, v) {
  try { win.localStorage.setItem('crm_prod_show_photos', v ? '1' : '0'); } catch (e) { /* best effort, come il v1 */ }
}
export function writePhotoSize(win, px) {
  try { win.localStorage.setItem('crm_prod_photo_size', String(px)); } catch (e) { /* best effort, come il v1 */ }
}

// Foto di riga: attrs.previewImage vince, poi il primo allegato immagine con
// dataUrl (parità _prodPhoto v1:7700-7703).
export function prodPhoto(p) {
  if (p.attrs && p.attrs.previewImage) return { dataUrl: p.attrs.previewImage, name: 'anteprima' };
  return (Array.isArray(p.attachments) ? p.attachments : [])
    .find((a) => (a.mimeType || '').startsWith('image/') && a.dataUrl) || null;
}

// Lettura dati: STESSA composizione di renderProducts v1 (proc-v0.js:7675-7713):
// pre-sort createdAt desc (tie-break per i prodotti senza quotazioni), colonne
// derivate (_supCount fornitori distinti dalle quotazioni, _lastQuoteAt max
// receivedAt||createdAt, _brand, _photoCount immagini negli allegati).
// PARITÀ ESATTA dell'ordine (default E click utente): l'ordine base v1 è il
// pre-sort createdAt desc; ogni colonna riceve il suo rank v1
// (assignV1SortRanks — stessi type del makeDataTable v1:7749-7757: number per
// foto/fornitori/schema, STRINGA per tutto il resto, 'Ult. quotazione'
// compresa che nel v1 confronta String(timestamp)). L'ordine di default
// mostrato è il desc del rank _lastQuoteAt = asc stabile + reverse() del v1
// (proc-v0.js:10866-10874), quirk sui pari merito INCLUSO (pinnato dal test).
export const PROD_SORT_COLS = [
  { key: '_photoCount', type: 'number' },
  { key: 'name' },
  { key: 'category' },
  { key: '_brand' },
  { key: '_supCount', type: 'number' },
  { key: '_lastQuoteAt', sortKey: (r) => r._lastQuoteAt }, // v1: sortKey senza type ⇒ confronto STRINGA
  { key: 'schemaVersion', type: 'number' },
];
export function readProducts(win) {
  const store = win.store;
  const list = store.listProducts().slice().sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  const rows = list.map((p) => {
    const qs = store.listQuotationsByProduct(p.id);
    return {
      ...p,
      _supCount: new Set(qs.map((q) => q.supplierId)).size,
      _lastQuoteAt: qs.reduce((m, q) => { const ts = new Date(q.receivedAt || q.createdAt || 0).getTime(); return (ts && ts > m) ? ts : m; }, 0),
      _brand: p.brand || '',
      _photoCount: (Array.isArray(p.attachments) ? p.attachments : []).filter((a) => (a.mimeType || '').startsWith('image/')).length,
    };
  });
  assignV1SortRanks(rows, PROD_SORT_COLS);
  // ordine di consegna = ordine di default v1 (rank _lastQuoteAt desc)
  return { rows: rows.slice().sort((a, b) => b._v1Rank._lastQuoteAt - a._v1Rank._lastQuoteAt) };
}

// prettyDate v1 (proc-v0.js:3144) per la colonna "Ult. quotazione" (v1:7755
// passa new Date(ts).toISOString(): c'è sempre l'ora ⇒ data + ' HH:MM' locali).
export function fmtQuoteTs(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + ' ' + d.toTimeString().slice(0, 5);
}

// --- Testi di match dei filtri per colonna (stripHtml(render) v1) ------------

// Foto: il render v1 ha testo SOLO nel badge conteggio (visibile con >1 foto).
export function photoFilterText(r) {
  return r._photoCount > 1 ? String(r._photoCount) : '';
}
// Nome: `<span>Nome</span> <span>KIT</span>` → strip = 'Nome KIT' (spazio TRA i tag).
export function prodNameFilterText(r) {
  return String(r.name || '') + ((r.attrs && r.attrs.isKit) ? ' KIT' : '');
}
export function lastQuoteFilterText(r) {
  return r._lastQuoteAt ? fmtQuoteTs(r._lastQuoteAt) : '—';
}

// --- Deleghe / azioni bulk (STESSE primitive v1: payload identici) -----------
// Le bulk di renderProducts sono closure inline nel v1 (non globali): qui si
// replicano CHIAMANDO le stesse primitive globali (quickPickCategory,
// ensureSentinelCategory, confirmWrite.product/deleteProduct, _bulkReport) e
// chiudendo con win.renderProducts() come il v1 (sotto flag è il wrapper v3).
// NB parità v1:7728/7739: l'annullamento (picker chiuso / confirm rifiutato)
// ritorna undefined, NON false ⇒ come nel v1 la selezione si azzera comunque.
export function openNewProduct(win) { win.openProductModal(null); }
export function openProductDetail(win, id) {
  win.currentProductId = id;            // pattern v1 (proc-v0.js:7759)
  win.showView('product-detail');       // il dettaglio RESTA v1 (confine dichiarato)
}
export function resolveProductSelection(win, ids) {
  return ids.map((id) => win.store.getProduct(id)).filter(Boolean);
}
export function buildProductBulkActions(win) {
  return [
    { label: PROD_TEXTS.bulkMoveCategory, fn: async (sel) => {
      const cat = await win.quickPickCategory({ productName: sel.map((p) => p.name).filter(Boolean).join(', ') });
      if (cat === undefined) return;
      const target = cat || await win.ensureSentinelCategory();
      // F1T5: sequenza CONFERMATA per-record con esito aggregato onesto (= v1:7731-7736)
      const failed = [];
      for (const p of sel) {
        const r = await win.confirmWrite.product(p.id, { category: target }, { quiet: true });
        if (!r.ok) failed.push(p.name);
      }
      win._bulkReport(`Spostati in "${target}"`, sel.length, failed);
      win.renderProducts();
    } },
    { label: PROD_TEXTS.bulkDelete, fn: async (sel) => {
      if (!win.confirm(`Eliminare ${sel.length} prodotti?`)) return;
      // F1T5: DELETE CONFERMATO per-record — su errore la riga NON sparisce (= v1:7741-7746)
      const failed = [];
      for (const p of sel) {
        const r = await win.confirmWrite.deleteProduct(p.id, { quiet: true });
        if (!r.ok) failed.push(p.name);
      }
      win._bulkReport('Eliminazione prodotti', sel.length, failed);
      win.renderProducts();
    } },
  ];
}

// Globali delegati dalla vista Prodotti (guardie install + test statico).
// confirmWrite è un OGGETTO esposto su window (proc-v0.js:4221): la guardia
// controlla i METODI usati (PROD_DELEGATED_METHODS), non typeof function.
export const PROD_DELEGATED_GLOBALS = [
  'showView', 'openProductModal', 'quickPickCategory', 'ensureSentinelCategory', '_bulkReport',
];
export const PROD_DELEGATED_METHODS = { confirmWrite: ['product', 'deleteProduct'] };

// ============================================================================
// F2.3d — vista "Campagne outreach" (renderCampaigns v1, proc-v0.js:7100-7148)
// ============================================================================

export const CMP_LABELS = {
  en: {
    'cmp.title': 'Campaigns',
    'cmp.subtitle': 'Regular sources broadly; Quick RFQ is one supplier, full sequence, zero setup.',
    'cmp.quick_rfq': 'Quick RFQ',
    'cmp.new': '+ New campaign',
    'col.name': 'Name',
    'col.kind': 'Kind',
    'col.priority': 'Priority',
    'col.target': 'Target',
    'col.sending_as': 'Sending as',
    'col.state': 'State',
    'col.suppliers': 'Suppliers',
    // dizionario stati di stateBadge v1 (proc-v0.js:3196): SOLO le chiavi che
    // esistono nel dict v1 — per le altre vale il fallback replace(/_/g,' ')
    'state.to_contact': 'to contact',
    'state.contacted': 'contacted',
    'state.replied': 'replied',
    'state.off_scope': 'off scope',
    'state.closed_does_not_carry': 'does not carry',
    'state.closed_won': 'closed won',
    'state.closed_lost': 'closed lost',
    'state.draft': 'draft',
    'state.confirmed': 'confirmed',
    'state.in_transit': 'in transit',
    'state.partial': 'partial',
    'state.received': 'received',
    'state.cancelled': 'cancelled',
    'state.awaiting': 'awaiting',
    'state.awaiting-decision': 'awaiting decision',
    'state.superseded': 'superseded',
    'state.expired': 'expired',
    'state.paused': 'paused',
    'state.closed': 'closed',
  },
  it: {
    'cmp.title': 'Campagne',
    'cmp.subtitle': 'Le campagne regolari sondano in modo ampio; Quick RFQ è un fornitore, sequenza completa, zero setup.',
    'cmp.quick_rfq': 'Quick RFQ',
    'cmp.new': '+ Nuova campagna',
    'col.name': 'Nome',
    'col.kind': 'Tipo',
    'col.priority': 'Priorità',
    'col.target': 'Target',
    'col.sending_as': 'Mittente',
    'col.state': 'Stato',
    'col.suppliers': 'Fornitori',
    'state.to_contact': 'da contattare',
    'state.contacted': 'contattato',
    'state.replied': 'ha risposto',
    'state.off_scope': 'fuori scope',
    'state.closed_does_not_carry': 'non lo tratta',
    'state.closed_won': 'vinto',
    'state.closed_lost': 'perso',
    'state.draft': 'bozza',
    'state.confirmed': 'confermato',
    'state.in_transit': 'in transito',
    'state.partial': 'parziale',
    'state.received': 'ricevuto',
    'state.cancelled': 'annullato',
    'state.awaiting': 'in attesa',
    'state.awaiting-decision': 'in attesa di decisione',
    'state.superseded': 'sostituito',
    'state.expired': 'scaduto',
    'state.paused': 'in pausa',
    'state.closed': 'chiuso',
  },
};

// Testi LETTERALI del v1 (hardcoded in renderCampaigns).
export const CMP_TEXTS = {
  emptyMessage: 'Nessuna campagna corrisponde ai filtri.',
  tagsCol: 'Tag',
  anyTag: 'Tutti i tag',
};
// Pill dei filtri (v1:7117): [id, label] VERBATIM, stesso ordine.
export const CMP_FILTER_PILLS = [
  ['all', 'Tutte'], ['regular', 'Regolare'], ['quick', 'RFQ rapida'], ['active', 'Attiva'], ['closed_won', 'Vinta'],
];

export function tCmp(win, key) {
  const lang = pickLang(win);
  const d = CMP_LABELS[lang] || CMP_LABELS.it;
  return d[key] != null ? d[key] : (CMP_LABELS.it[key] != null ? CMP_LABELS.it[key] : key);
}

// Stato dei filtri di vista — DEFAULT identici ai binding lessicali del v1
// (campFilter='all', campTagFilter='' — proc-v0.js:7098-7099).
export function defaultCampaignFilters() {
  return { filter: 'all', tagFilter: '' };
}

// stateBadge v1 (proc-v0.js:3179-3201): mappa classi + label dal dict (chiave
// 'state.<x>' se esiste, altrimenti lo stato con '_'→' ').
export const STATE_BADGE_CLASSES = {
  'to_contact': 'bg-slate-100 text-slate-700',
  'contacted': 'bg-cyan-100 text-cyan-800',
  'replied': 'bg-emerald-100 text-emerald-800',
  'closed_won': 'bg-emerald-200 text-emerald-900',
  'closed_lost': 'bg-slate-200 text-slate-700',
  'closed_does_not_carry': 'bg-slate-200 text-slate-700',
  'off_scope': 'bg-purple-100 text-purple-800',
  'active': 'bg-emerald-100 text-emerald-800',
  'awaiting-decision': 'bg-amber-100 text-amber-800',
  'waiting': 'bg-slate-100 text-slate-700',
  'closed': 'bg-slate-100 text-slate-500',
  'superseded': 'bg-slate-100 text-slate-500',
  'expired': 'bg-rose-100 text-rose-800',
};
export function stateBadgeClass(state) {
  return STATE_BADGE_CLASSES[state] || 'bg-slate-100 text-slate-700';
}
export function stateLabel(win, state) {
  const lang = pickLang(win);
  const d = CMP_LABELS[lang] || CMP_LABELS.it;
  const key = 'state.' + state;
  return d[key] != null ? d[key] : String(state || '').replace(/_/g, ' ');
}

// Lettura dati: STESSA composizione di renderCampaigns v1 (proc-v0.js:7100-7126):
// listCampaigns con la STESSA mappa argomenti dal filtro pill, poi filtro tag
// client-side, allCampTags da TUTTE le campagne (localeCompare), colonne
// derivate (_supCount, _identityName con fallback '?', _identityEmail).
// PARITÀ ESATTA dell'ordine (default E click utente): ogni colonna riceve il
// suo rank v1 (assignV1SortRanks, stessi type del v1:7130-7138 — number solo
// _supCount; 'tags' è un array ⇒ String(array) come nel v1). L'ordine di
// default è il rank di name asc (confronto `<` su lowercase, stabile, senza
// reverse) — nessuna deriva localeCompare ('10' < '2' come nel v1).
export const CMP_SORT_COLS = [
  { key: 'name' },
  { key: 'kind' },
  { key: 'priority' },
  { key: 'target' },
  { key: 'tags' },
  { key: '_identityName' },
  { key: 'state' },
  { key: '_supCount', type: 'number' },
];
export function readCampaigns(win, filters) {
  const store = win.store;
  const f = filters || defaultCampaignFilters();
  const _allList = store.listCampaigns(
    f.filter === 'all' ? {}
      : f.filter === 'quick' ? { kind: 'quick' }
        : f.filter === 'regular' ? { kind: 'regular' }
          : { state: f.filter },
  );
  const list = f.tagFilter ? _allList.filter((c) => (c.tags || []).includes(f.tagFilter)) : _allList;
  const allCampTags = [...new Set(store.listCampaigns({}).flatMap((c) => c.tags || []))].sort((a, b) => a.localeCompare(b));
  const rows = list.map((c) => {
    const css = store.listCampaignSuppliers(c.id);
    const identity = store.getIdentity(c.identityId);
    return {
      ...c,
      _supCount: css.length,
      _identityName: (identity && identity.name) || '?',
      _identityEmail: (identity && identity.email) || '',
    };
  });
  assignV1SortRanks(rows, CMP_SORT_COLS);
  // ordine di consegna = ordine di default v1 (rank di name, asc)
  return { rows: rows.slice().sort((a, b) => a._v1Rank.name - b._v1Rank.name), allCampTags };
}

// --- Testi di match dei filtri per colonna (stripHtml(render) v1) ------------
export function cmpTargetFilterText(r) { return r.target || '—'; }
// Tag: badge concatenati SENZA separatore (v1:7135 join('')), '—' se vuoto.
export function cmpTagsFilterText(r) { return (r.tags && r.tags.length) ? r.tags.join('') : '—'; }
// Mittente: nome + email concatenati SENZA spazio (il div non aggiunge testo).
export function cmpIdentityFilterText(r) { return String(r._identityName || '') + String(r._identityEmail || ''); }
export function cmpStateFilterText(win, r) { return stateLabel(win, r.state); }

// --- Deleghe al v1 ------------------------------------------------------------
export function openQuickRfq(win) { win.openCampaignModal({ kind: 'quick' }); }
export function openNewCampaign(win) { win.openCampaignModal({ kind: 'regular' }); }
export function openCampaignDetail(win, id) {
  win.currentCampaignId = id;           // pattern v1 (proc-v0.js:7141)
  win.showView('campaign-detail');      // il dettaglio RESTA v1 (confine dichiarato)
}

export const CMP_DELEGATED_GLOBALS = ['showView', 'openCampaignModal'];

// ============================================================================
// F2.3e — vista "Conversazioni" (renderConversations v1, proc-v0.js:7024-7095)
// ============================================================================
// STUDIO DELL'ACCOPPIAMENTO (mandato ondata 20 — la vista era stata scartata
// in ondata 19 per "badge + accoppiamento maggiore"; verificato sul monolite):
//  - BADGE nav (.conv-counter): lo scrive updateConvCounter (proc-v0.js:16530),
//    che vive FUORI dalla vista (elementi della sidebar) — il wrap di
//    renderConversations NON lo tocca, resta v1 con qualunque flag.
//  - POLLER globale startConversationsPoll (proc-v0.js:16536): ogni 6s, se i
//    dati cambiano, aggiorna DB.conversations e chiama renderConversations()
//    quando currentView==='conversations'. L'identificatore si risolve sul
//    global object A OGNI CHIAMATA (stesso principio del wrap, install.js):
//    sotto flag il redraw del poller arriva al v3, che rilegge lo store.
//    Il poller NON va wrappato né duplicato.
//  - Action Center: il poller aggiorna anche i suoi contatori, ma è codice del
//    poller, non della vista. Nessun accoppiamento nel render.
//  - Click riga: openConversationModal (MODALE, non showView) — il v3 resta
//    montato sotto il modale, come il v1 resta disegnato. Le scritture del
//    modale rientrano via confirmWrite → renderConversations() → wrap → v3.
// CONCLUSIONE: il wrap pulito REGGE — nessuna spike necessaria.

// LABEL dal dizionario I18N v1 (VERBATIM, test di sync come per le altre viste).
export const CONV_LABELS = {
  en: {
    'conv.title': 'Conversations',
    'conv.subtitle': 'Each conversation is between us and a contact. Multi-word AND search in headers.',
    'conv.all_states': 'All states',
    'conv.awaiting_decision': 'Awaiting decision',
    'conv.active': 'Active',
    'conv.waiting_reply': 'Waiting reply',
    'conv.closed': 'Closed',
    'col.contact': 'Contact',
    'col.supplier': 'Supplier',
    'col.subject': 'Subject',
    'col.linked_to': 'Linked to',
    'col.last_activity': 'Last activity',
    'col.state': 'State',
    'time.just_now': 'just now',
    'time.min_ago': '{n}m ago',
    'time.h_ago': '{n}h ago',
    'time.d_ago': '{n}d ago',
    'time.in_min': 'in {n}m',
    'time.in_h': 'in {n}h',
    'time.in_d': 'in {n}d',
  },
  it: {
    'conv.title': 'Conversazioni',
    'conv.subtitle': 'Ogni conversazione è tra noi e un contatto. Ricerca multi-parola AND negli header.',
    'conv.all_states': 'Tutti gli stati',
    'conv.awaiting_decision': 'In attesa di decisione',
    'conv.active': 'Attiva',
    'conv.waiting_reply': 'In attesa di risposta',
    'conv.closed': 'Chiusa',
    'col.contact': 'Contatto',
    'col.supplier': 'Fornitore',
    'col.subject': 'Oggetto',
    'col.linked_to': 'Collegato a',
    'col.last_activity': 'Ultima attività',
    'col.state': 'Stato',
    'time.just_now': 'adesso',
    'time.min_ago': '{n} min fa',
    'time.h_ago': '{n} h fa',
    'time.d_ago': '{n} g fa',
    'time.in_min': 'tra {n} min',
    'time.in_h': 'tra {n} h',
    'time.in_d': 'tra {n} g',
  },
};

// Testi LETTERALI del v1 (hardcoded in renderConversations, in ENTRAMBE le
// lingue — 'nessuna campagna' della cella è il letterale di v1:7056/7069,
// NON la chiave conv.no_campaign del dict).
export const CONV_TEXTS = {
  newBtn: '+ Nuova conversazione',
  archivedOption: 'Archiviate',
  tagsCol: 'Tag',
  noCampaign: 'nessuna campagna',
  emptyMessage: 'Nessuna conversazione.',
  addTagBtn: '+ tag',
  removeTagTitle: 'Rimuovi',
  tagPrompt: 'Nuovo tag per la conversazione:',
  receivedWord: 'ricevuto',
};

export function tConv(win, key) {
  const lang = pickLang(win);
  const d = CONV_LABELS[lang] || CONV_LABELS.it;
  return d[key] != null ? d[key] : (CONV_LABELS.it[key] != null ? CONV_LABELS.it[key] : key);
}

// Stato del filtro di vista — DEFAULT identico al binding lessicale del v1
// (convFilters = { state: 'all' } — proc-v0.js:7023). Come per le campagne
// (deviazione 5 f23d): binding LESSICALE irraggiungibile ⇒ v1 e v3 non
// condividono il valore corrente, stessi default.
export function defaultConvFilters() {
  return { state: 'all' };
}

// prettyAgo v1 (proc-v0.js:3123-3139) con NOW() = new Date() (proc-v0.js:10)
// e le chiavi time.* del dict (interpolazione {n} come I18N.t v1:1218).
export function convPrettyAgo(win, iso) {
  if (!iso) return '—';
  const tt = (key, n) => String(tConv(win, key)).replace('{n}', String(n));
  const then = new Date(iso);
  const now = new Date();
  const diffMin = Math.round((now - then) / 60000);
  if (diffMin < 0) {
    const a = -diffMin;
    if (a < 60) return tt('time.in_min', a);
    const ah = Math.round(a / 60);
    if (ah < 24) return tt('time.in_h', ah);
    return tt('time.in_d', Math.round(ah / 24));
  }
  if (diffMin < 1) return tConv(win, 'time.just_now');
  if (diffMin < 60) return tt('time.min_ago', diffMin);
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return tt('time.h_ago', diffH);
  return tt('time.d_ago', Math.round(diffH / 24));
}

// _cleanSubject v1 (proc-v0.js:6138-6145) VERBATIM: toglie i prefissi
// re/fwd/aw/rif ripetuti e ne conserva UNO normalizzato.
export function cleanSubject(s) {
  let str = String(s || '').trim();
  let prefix = '';
  const re = /^\s*(re|r|fwd|fw|i|aw|rif)\s*:\s*/i;
  let m;
  while ((m = str.match(re))) {
    if (!prefix && /fwd|fw/i.test(m[1])) prefix = 'Fwd: ';
    else if (!prefix) prefix = 'Re: ';
    str = str.slice(m[0].length);
  }
  return (prefix + str).trim();
}

// Lettura dati: STESSA composizione di renderConversations v1 (proc-v0.js:
// 7025-7061): listConversations con lo stesso argomento state (undefined per
// all/archived), filtro archiviate (visibili SOLO col filtro 'archived'),
// colonne derivate (contatto/lingua, fornitore, campagne collegate, età
// relativa, stato). PARITÀ ESATTA dell'ordine (default E click utente): base =
// ordine dello store (lastActivityAt desc, v1:2322), rank v1 per colonna
// (tutti i type del makeDataTable v1:7065-7075 — 'date' SOLO su Ultima
// attività via sortKey lastActivityAt; 'tags' è un array ⇒ String(array));
// l'ordine di consegna è il rank _lastActivityRel desc = asc stabile +
// reverse() del v1 (quirk sui pari merito INCLUSO).
export const CONV_SORT_COLS = [
  { key: '_contactName' },
  { key: '_supplierName' },
  { key: 'subject' },
  { key: '_campaignsText' },
  { key: '_lastActivityRel', sortKey: (r) => r.lastActivityAt, type: 'date' },
  { key: '_stateText' },
  { key: 'tags' },
];
export function readConversations(win, filters) {
  const store = win.store;
  const f = filters || defaultConvFilters();
  let list = store.listConversations({ state: (f.state === 'all' || f.state === 'archived') ? undefined : f.state });
  // Archiviate: nascoste di default, visibili solo col filtro "archiviate" (v1:7027)
  list = (f.state === 'archived') ? list.filter((c) => c.archived) : list.filter((c) => !c.archived);
  const rows = list.map((cv) => {
    const sup = store.getSupplier(cv.supplierId);
    const ct = store.getContact(cv.contactId);
    const camps = (cv.linkedCampaignIds || []).map((id) => store.getCampaign(id)).filter(Boolean);
    return {
      ...cv,
      _contactName: (ct && ct.name) || '?',
      _contactLang: (ct && ct.language) || '',
      _supplierName: (sup && sup.name) || '?',
      _campaignsText: camps.map((c) => c.name).join(', ') || CONV_TEXTS.noCampaign,
      _campaigns: camps,
      _lastActivityRel: convPrettyAgo(win, cv.lastActivityAt),
      _stateText: cv.state || '',
    };
  });
  assignV1SortRanks(rows, CONV_SORT_COLS);
  // ordine di consegna = ordine di default v1 (rank _lastActivityRel, desc)
  return { rows: rows.slice().sort((a, b) => b._v1Rank._lastActivityRel - a._v1Rank._lastActivityRel) };
}

// Evidenziazione riga v1 (proc-v0.js:7078): in entrata e non chiusa = ambra.
export function convRowClass(r) {
  return (r.lastDirection === 'in' && r.state !== 'closed') ? 'bg-amber-50 hover:bg-amber-100' : '';
}

// Cella Oggetto (v1:7068): titolo (se non vuoto) o subject ripulito; sotto,
// conteggio messaggi + canale (+ subject ripulito se il titolo c'è).
export function convSubjectParts(r) {
  const primary = (r.title && r.title.trim()) ? r.title : cleanSubject(r.subject || '');
  const chan = r.channel === 'email' ? 'email' : r.channel === 'whatsapp' ? 'WA' : 'phone';
  const secondary = String(r.messageCount || 0) + ' msgs · ' + chan + (r.title ? ' · ' + cleanSubject(r.subject || '') : '');
  return { primary, secondary };
}

// --- Testi di match dei filtri per colonna (stripHtml(render) v1) ------------
// Contatto: due div adiacenti ⇒ nome+lingua concatenati SENZA spazio.
export function convContactFilterText(r) {
  return String(r._contactName == null ? '' : r._contactName) + (r._contactLang ? String(r._contactLang) : '');
}
// Oggetto: due div adiacenti ⇒ primario+secondario concatenati SENZA spazio.
export function convSubjectFilterText(r) {
  const p = convSubjectParts(r);
  return p.primary + p.secondary;
}
// Collegato a: badge nome-campagna concatenati (join('')), else letterale.
export function convCampaignsFilterText(r) {
  return (r._campaigns && r._campaigns.length) ? r._campaigns.map((c) => c.name).join('') : CONV_TEXTS.noCampaign;
}
// Ultima attività: '● ricevuto <rel>' in entrata, '→ <rel>' in uscita.
export function convLastActivityFilterText(r) {
  return (r.lastDirection === 'in' ? '● ' + CONV_TEXTS.receivedWord : '→') + ' ' + r._lastActivityRel;
}
export function convStateFilterText(win, r) { return stateLabel(win, r.state); }

// --- Deleghe / azioni al v1 (payload identici: confirmWrite del monolite) ----
// Il click riga apre il MODALE della conversazione (v1:7079) — non showView:
// il v3 resta montato sotto il modale, esattamente come il v1 resta disegnato.
export function openConversation(win, id) { win.openConversationModal(id); }
export function openNewConversation(win) { win.openNewConversationModal(); }

// Tag inline (v1:7087-7088, F1T5 confermato): prompt → dedupe → confirmWrite
// .conversation(id, { tags }) e redraw SOLO su r.ok (su errore il toast lo fa
// confirmWrite stesso, non-quiet — la riga resta con i tag di prima).
export function addConvTag(win, id) {
  const tag = (win.prompt(CONV_TEXTS.tagPrompt) || '').trim();
  if (!tag) return;
  const cv = win.store.getConversation(id);
  if (!cv) return;
  const tags = Array.from(new Set([...(cv.tags || []), tag]));
  return win.confirmWrite.conversation(id, { tags }).then((r) => { if (r.ok) win.renderConversations(); });
}
export function removeConvTag(win, id, tag) {
  const cv = win.store.getConversation(id);
  if (!cv) return;
  const tags = (cv.tags || []).filter((x) => x !== tag);
  return win.confirmWrite.conversation(id, { tags }).then((r) => { if (r.ok) win.renderConversations(); });
}

// Globali/metodi delegati dalla vista Conversazioni (guardie install + test
// statico). confirmWrite e store sono OGGETTI su window: guardia sui metodi.
export const CONV_DELEGATED_GLOBALS = ['openConversationModal', 'openNewConversationModal'];
export const CONV_DELEGATED_METHODS = {
  confirmWrite: ['conversation'],
  store: ['listConversations', 'getConversation', 'getSupplier', 'getContact', 'getCampaign'],
};

// ============================================================================
// F2.3e — vista "Ordini" (renderOrders v1, proc-v0.js:11002-11055)
// ============================================================================

// LABEL dal dizionario I18N v1 (VERBATIM). NB: sottotitolo e bottone della
// vista NON vengono dal dict (v1:11009/11011 hardcoded) — stanno in ORD_TEXTS.
export const ORD_LABELS = {
  en: {
    'ord.title': 'Orders',
    'col.po_number': 'PO #',
    'col.supplier': 'Supplier',
    'col.lines': 'Lines',
    'col.total': 'Total',
    'col.status': 'Status',
    'col.created': 'Created',
  },
  it: {
    'ord.title': 'Ordini',
    'col.po_number': '# Ordine',
    'col.supplier': 'Fornitore',
    'col.lines': 'Righe',
    'col.total': 'Totale',
    'col.status': 'Stato',
    'col.created': 'Creato',
  },
};

// Testi LETTERALI del v1 (hardcoded in renderOrders).
export const ORD_TEXTS = {
  subtitle: 'Ordini di acquisto. Ciclo di vita: bozza → confermato → in transito → parziale / ricevuto.',
  newBtn: '+ Nuovo ordine',
  expectedCol: 'Atteso',
  emptyMessage: 'Nessun ordine — clicca + Nuovo ordine per iniziare.',
  bulkConfirm: 'Segna confermati',
  bulkDelete: 'Elimina',
};

export function tOrd(win, key) {
  const lang = pickLang(win);
  const d = ORD_LABELS[lang] || ORD_LABELS.it;
  return d[key] != null ? d[key] : (ORD_LABELS.it[key] != null ? ORD_LABELS.it[key] : key);
}

// _orderStatusBadge v1 (proc-v0.js:11057-11067): mappa classi + label = stato
// con la PRIMA '_' sostituita da spazio (String.replace con stringa — quirk
// v1 conservato; NESSUNA traduzione dal dict, a differenza di stateBadge).
export const ORD_STATUS_BADGE_CLASSES = {
  draft: 'bg-slate-100 text-slate-700',
  confirmed: 'bg-cyan-100 text-cyan-800',
  in_transit: 'bg-amber-100 text-amber-800',
  partial: 'bg-purple-100 text-purple-800',
  received: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-rose-100 text-rose-800',
};
export function ordStatusBadgeClass(status) {
  return ORD_STATUS_BADGE_CLASSES[status] || 'bg-slate-100 text-slate-700';
}
export function ordStatusLabel(status) {
  return String(status == null ? '' : status).replace('_', ' ');
}

// Lettura dati: STESSA composizione di renderOrders v1 (proc-v0.js:11003-11020).
// FONTE DEI TOTALI (mandato F2.3e): _total = win.store.orderTotal(o) — la
// STESSA funzione del v1 (proc-v0.js:3001, riduzione su o.items) sugli STESSI
// oggetti ordine che l'API consegna via orderOut (F1.3 FASE C: items letti
// dalle figlie, server-side). Il v3 NON ricalcola per conto suo: qualunque
// cambiamento di fonte (orderOut) arriva identico a v1 e v3.
// PARITÀ ESATTA dell'ordine: base = ordine dello store (createdAt desc,
// v1:2952), rank v1 per colonna (number su Righe/Totale, date su Creato/
// Atteso, stringa sul resto); consegna = rank createdAt desc (asc stabile +
// reverse del v1, pari merito inclusi).
export const ORD_SORT_COLS = [
  { key: 'code' },
  { key: '_supplierName' },
  { key: '_itemCount', type: 'number' },
  { key: '_total', type: 'number' },
  { key: 'status' },
  { key: 'createdAt', type: 'date' },
  { key: 'expectedDate', type: 'date' },
];
export function readOrders(win) {
  const store = win.store;
  const orders = store.listOrders();
  const rows = orders.map((o) => {
    const sup = store.getSupplier(o.supplierId);
    return {
      ...o,
      _supplierName: (sup && sup.name) || '?',
      _itemCount: (o.items || []).length,
      _total: store.orderTotal(o), // STESSA fonte del v1 — mai un ricalcolo v3
    };
  });
  assignV1SortRanks(rows, ORD_SORT_COLS);
  // ordine di consegna = ordine di default v1 (rank createdAt, desc)
  return { rows: rows.slice().sort((a, b) => b._v1Rank.createdAt - a._v1Rank.createdAt) };
}

// --- Testi di match dei filtri per colonna (stripHtml(render) v1) ------------
export function ordTotalFilterText(r) {
  return String(r.currency) + ' ' + Number(r._total).toLocaleString();
}
export function ordStatusFilterText(r) { return ordStatusLabel(r.status); }
// Creato/Atteso: prettyDate(..).slice(0,10) del v1 = fmtCreated (data GG/MM/AAAA,
// '—' sui vuoti: v1 controlla la truthiness prima — output identico).
export function ordCreatedFilterText(r) { return fmtCreated(r.createdAt); }
export function ordExpectedFilterText(r) { return fmtCreated(r.expectedDate); }

// --- Deleghe / azioni bulk (STESSE primitive v1: payload identici) -----------
export function openNewOrder(win) { win.openOrderModal(); }
export function openOrderDetail(win, id) {
  win.currentOrderId = id;           // pattern v1 (proc-v0.js:11052)
  win.showView('order-detail');      // il dettaglio RESTA v1 (confine dichiarato)
}
export function resolveOrderSelection(win, ids) {
  return ids.map((id) => win.store.getOrder(id)).filter(Boolean);
}
// Bulk di renderOrders (v1:11025-11041, F1T3): sequenza CONFERMATA per-record
// ({quiet:true}), esito aggregato onesto via toast (testi VERBATIM), redraw
// con win.renderOrders() (sotto flag è il wrapper v3). NB parità: l'annullo
// del confirm di Elimina ritorna undefined, NON false ⇒ come nel v1 la
// selezione si azzera comunque (quirk S4 f23d, conservato).
export function buildOrderBulkActions(win) {
  return [
    { label: ORD_TEXTS.bulkConfirm, fn: async (sel) => {
      let ok = 0, ko = 0;
      for (const o of sel) {
        const r = await win.confirmWrite.order(o.id, { status: 'confirmed' }, { quiet: true });
        if (r.ok) ok++; else ko++;
      }
      win.toast(ko ? `⚠️ ${ok} ordini confermati · ${ko} NON salvati (riprova)` : `${ok} ordini → confirmed`, ko ? 'error' : 'success');
      win.renderOrders();
    } },
    { label: ORD_TEXTS.bulkDelete, fn: async (sel) => {
      if (!win.confirm(`Eliminare ${sel.length} ordini?`)) return;
      let ok = 0, ko = 0;
      for (const o of sel) {
        const r = await win.confirmWrite.deleteOrder(o.id, { quiet: true });
        if (r.ok) ok++; else ko++;
      }
      win.toast(ko ? `⚠️ ${ok} ordini eliminati · ${ko} NON eliminati (le righe restano)` : `${ok} ordini eliminati`, ko ? 'error' : 'success');
      win.renderOrders();
    } },
  ];
}

// Globali/metodi delegati dalla vista Ordini (guardie install + test statico).
export const ORD_DELEGATED_GLOBALS = ['showView', 'openOrderModal', 'toast'];
export const ORD_DELEGATED_METHODS = {
  confirmWrite: ['order', 'deleteOrder'],
  store: ['listOrders', 'getOrder', 'getSupplier', 'orderTotal'],
};

// ============================================================================
// F2.3f — vista "Tag" (renderTags v1, proc-v0.js:8974-9019)
// ============================================================================
// SCELTA (mandato ondata 21, motivata in evidence): tra le viste RESTANTI del
// monolite, Tag è tabellare pura (makeDataTable, v1:8996) con dati reali in
// prod (16 tag) e una scrittura vera (+ Nuovo tag → confirmWrite.createTag)
// per la parità payload. Le candidate Monitoraggio/Templates sono card-grid
// (non tabellari), "Kit" non è una vista (vive nel product-detail) e "Coda
// invii" è un MODALE (openSendQueueModal), non una voce di navigazione.

// LABEL dal dizionario I18N v1 (VERBATIM, test di sync come per le altre viste).
export const TAG_LABELS = {
  en: {
    'tag.title': 'Tags',
    'tag.subtitle': 'Flat second axis. Global (type:distributor) or scoped (brand:Solis @ inverter/string). Click a tag for a full panorama.',
    'tag.new': '+ New tag',
    'col.tag': 'Tag',
    'col.prefix': 'Prefix',
    'col.pinned_category': 'Pinned category',
    'col.source': 'Source',
    'col.suppliers_using': 'Suppliers using',
    'toast.added': 'Added',
    'toast.already_exists': 'Already exists',
  },
  it: {
    'tag.title': 'Tag',
    'tag.subtitle': 'Asse flat secondario. Globali (type:distributor) o specifici (brand:Solis @ inverter/string). Clicca un tag per il panorama completo.',
    'tag.new': '+ Nuovo tag',
    'col.tag': 'Tag',
    'col.prefix': 'Prefisso',
    'col.pinned_category': 'Categoria specifica',
    'col.source': 'Fonte',
    'col.suppliers_using': 'Fornitori che lo usano',
    'toast.added': 'Aggiunto',
    'toast.already_exists': 'Esiste già',
  },
};

// Testi LETTERALI del v1 (hardcoded in renderTags, fuori dal dict I18N).
export const TAG_TEXTS = {
  emptyMessage: 'Nessun tag ancora.',
  newTagPrompt: 'Nome tag (es. type:distributore, vip, brand:Solis @ inverter/string):',
};

export function tTag(win, key) {
  const lang = pickLang(win);
  const d = TAG_LABELS[lang] || TAG_LABELS.it;
  return d[key] != null ? d[key] : (TAG_LABELS.it[key] != null ? TAG_LABELS.it[key] : key);
}

// Lettura dati: STESSA composizione di renderTags v1 (proc-v0.js:8975-8998):
// listTags() (che include i tag "auto" presenti sui fornitori ma non in
// DB.tags) con pre-sort usageCount desc (sort STABILE: pari merito in ordine
// DB.tags), colonne derivate _prefix/_scope (parsing di v1:8988-8993), id =
// name (v1:8998). PARITÀ ESATTA dell'ordine (default E click utente): rank v1
// per colonna (number solo usageCount), consegna = rank usageCount desc
// (asc stabile + reverse del v1, quirk pari merito INCLUSO).
export const TAG_SORT_COLS = [
  { key: 'name' },
  { key: '_prefix' },
  { key: '_scope' },
  { key: 'kind' },
  { key: 'usageCount', type: 'number' },
];
export function readTags(win) {
  const store = win.store;
  const list = store.listTags().sort((a, b) => b.usageCount - a.usageCount);
  const rows = list.map((t) => {
    let prefix = '—', scope = '';
    if (t.name.includes(':')) {
      prefix = t.name.split(':')[0];
      const rest = t.name.slice(prefix.length + 1);
      if (rest.includes('@')) scope = rest.split('@')[1].trim();
    }
    return { ...t, _prefix: prefix, _scope: scope, _id: t.name, id: t.name };
  });
  assignV1SortRanks(rows, TAG_SORT_COLS);
  // ordine di consegna = ordine di default v1 (rank usageCount, desc)
  return { rows: rows.slice().sort((a, b) => b._v1Rank.usageCount - a._v1Rank.usageCount) };
}

// --- Testi di match dei filtri per colonna (stripHtml(render) v1) ------------
// Colonna Tag: tagBadge rende `${body}` + ` <span>@ ${scope}</span>` ⇒ lo
// strip ricompone ESATTAMENTE il nome ('body @ scope' oppure 'body').
export function tagNameFilterText(r) {
  const p = tagParts(r.name);
  return p.body + (p.scope ? ' @ ' + p.scope : '');
}
// Categoria specifica: code con lo scope, altrimenti lo span '—'.
export function tagScopeFilterText(r) { return r._scope ? r._scope : '—'; }

// --- Deleghe / azioni al v1 (payload identici: confirmWrite del monolite) ----
export function openTagDetail(win, name) {
  win.currentTagName = name;           // pattern v1 (proc-v0.js:9007)
  win.showView('tag-detail');          // il dettaglio RESTA v1 (confine dichiarato)
}
// "+ Nuovo tag" (v1:9010-9018, F1T5 confermato): prompt → dedupe su DB.tags →
// confirmWrite.createTag(name) e toast/redraw SOLO su r.ok.
export async function addNewTag(win) {
  const name = win.prompt(TAG_TEXTS.newTagPrompt, '');
  if (!name) return;
  if (win.DB.tags.find((x) => x.name === name)) { win.toast(tTag(win, 'toast.already_exists'), 'error'); return; }
  const r = await win.confirmWrite.createTag(name);
  if (!r.ok) return;
  win.toast(tTag(win, 'toast.added'), 'success'); win.renderTags();
}

export const TAG_DELEGATED_GLOBALS = ['showView', 'toast'];
export const TAG_DELEGATED_METHODS = {
  confirmWrite: ['createTag'],
  store: ['listTags'],
};

// ============================================================================
// F2.3f — vista "Magazzino" (renderWarehouse v1, proc-v0.js:11597-11751)
// ============================================================================
// SCELTA (mandato ondata 21): Magazzino è l'unica candidata del mandato
// REALMENTE tabellare (4 makeDataTable: giacenze live, log movimenti, ordini
// pendenti, ricevute recenti) ed è la vista operativa del magazzino fisico.
// La vista è a 5 TAB: live/log/orders usano il DataTable condiviso; "Aree" è
// una card-list replicata; "DDT & Partite" DELEGA il mount al modulo v1
// window.WarehouseDDT (warehouse-ddt.js) con lo STESSO fallback del v1.
// STATO CONDIVISO v1↔v3: il tab attivo è window._whTab e il raggruppamento
// del log è window._whGroup (globali del monolite, v1:11598/11634) — il v3
// legge e scrive GLI STESSI globali: cambiando flag si resta sullo stesso tab.

// LABEL dal dizionario I18N v1 (VERBATIM). Il resto della vista è hardcoded.
export const WH_LABELS = {
  en: {
    'wh.title': 'Warehouse',
    'col.po_number': 'PO #',
    'col.supplier': 'Supplier',
    'col.lines': 'Lines',
    'col.status': 'Status',
    'col.date': 'Date',
  },
  it: {
    'wh.title': 'Magazzino',
    'col.po_number': '# Ordine',
    'col.supplier': 'Fornitore',
    'col.lines': 'Righe',
    'col.status': 'Stato',
    'col.date': 'Data',
  },
};

// Testi LETTERALI del v1 (hardcoded in renderWarehouse, in ENTRAMBE le lingue).
export const WH_TEXTS = {
  btnIn: '+ Entrata',
  btnOut: '− Uscita',
  btnMove: '↔ Sposta',
  tabLive: 'Giacenze (live)',
  tabLog: 'Entrate / Uscite',
  tabDdt: 'DDT & Partite',
  tabOrders: 'Ricevimenti ordini',
  tabAreas: 'Aree',
  livePhotoCol: 'Foto',
  liveProductCol: 'Prodotto',
  liveLocationCol: 'Area di allocamento',
  liveQtyCol: 'Giacenza',
  liveLastCol: 'Ultimo mov.',
  liveNotAllocated: '— non allocato',
  liveDeleted: '(prodotto eliminato)',
  liveEmpty: 'Nessuna giacenza. Registra un\'entrata col pulsante Entrata.',
  groupToggle: 'Raggruppa per gruppo (entrata/uscita)',
  logDeleted: '(eliminato)',
  // NB: nel v1 la colonna Data del LOG è HARDCODED (label: 'Data', v1:11674),
  // NON t('col.date') — quella chiave del dict la usa SOLO la tabella
  // "Ricevute recenti" (v1:11742). Con utente EN il log resta 'Data'.
  logDateCol: 'Data',
  logTypeCol: 'Tipo',
  logQtyCol: 'Q.tà',
  logAreaCol: 'Area',
  logDestCol: 'Destinazione',
  logSourceCol: 'Fonte',
  logCodeCol: 'Codice',
  logNotesCol: 'Note',
  logPhotosCol: 'Foto',
  logIn: 'ENTRATA',
  logOut: 'USCITA',
  logMixed: 'MISTO',
  logNoGroup: 'Senza gruppo',
  logEmpty: 'Nessun movimento registrato.',
  logDelTitle: 'Elimina movimento',
  logEnteredTitle: 'inserito nell\'unità alternativa',
  deleteConfirm: 'Eliminare questo movimento?',
  ddtFallback: 'Modulo DDT non ancora caricato. Ricarica la pagina.',
  ordersPendingH: 'Ordini in attesa di ricevimento',
  ordersRecentH: 'Ricevute recenti',
  ordersExpectedCol: 'Atteso',
  ordersReceiveBtn: 'Ricevi',
  ordersPendingEmpty: 'Nessun ordine in attesa di ricevimento.',
  ordersDdtCol: 'DDT n°',
  ordersItemsCol: 'Articoli',
  ordersPhotoCol: 'Foto',
  ordersPhotoView: 'Visualizza',
  ordersRecentEmpty: 'Nessuna ricevuta registrata.',
  areasNotAllocated: '(non allocato)',
  areasRename: 'Rinomina',
  areasProductCol: 'Prodotto',
  areasQtyCol: 'Giacenza',
  areasEmptyArea: 'Area vuota (nessuna giacenza).',
  areasEmpty: 'Nessuna area. Le aree si creano registrando le entrate.',
  renamePrompt: 'Nuovo nome area (per unirla a un\'altra, usa lo stesso nome):',
  renameKoPrefix: 'Rinomina non sincronizzata: ',
  renameOk: 'Area rinominata',
};

export function tWh(win, key) {
  const lang = pickLang(win);
  const d = WH_LABELS[lang] || WH_LABELS.it;
  return d[key] != null ? d[key] : (WH_LABELS.it[key] != null ? WH_LABELS.it[key] : key);
}

// Tab attivo / raggruppamento log: GLI STESSI globali del v1 (mai binding propri).
export function whTab(win) { return win._whTab || 'live'; }
export function whSetTab(win, k) { win._whTab = k; }
export function whGroup(win) { return !!win._whGroup; }
export function whSetGroup(win, v) { win._whGroup = v; }

// _prodUnits v1 (proc-v0.js:11766-11769) VERBATIM.
export function whProdUnits(p) {
  const a = (p && p.attrs) || {};
  return { unit: a.unit || 'pz', altUnit: a.altUnit || null, altFactor: (a.altFactor && a.altFactor > 0) ? a.altFactor : null };
}
// _prodImg locale di renderWarehouse (v1:11621): primo allegato immagine con dataUrl.
export function whProdImg(p) {
  return (p && (Array.isArray(p.attachments) ? p.attachments : []).find((a) => (a.mimeType || '').startsWith('image/') && a.dataUrl)) || null;
}
// prettyDate v1 (proc-v0.js:3144-3149) VERBATIM: it-IT GG/MM/AAAA + ora solo
// per i datetime (ISO 'T' o SQL) — i call-site v1 fanno .slice(0,10)/.slice(0,16).
export function whPrettyDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
    + (/[T ]\d{2}:\d{2}/.test(String(iso)) ? ' ' + d.toTimeString().slice(0, 5) : '');
}
// prettyAgo della colonna "Ultimo mov.": stessa funzione del v1 (proc-v0.js:
// 3123) — riusa convPrettyAgo (le chiavi time.* sono le stesse del dict,
// già pinnate VERBATIM dal contract test conversazioni).
export function whPrettyAgo(win, iso) { return convPrettyAgo(win, iso); }

// --- Letture dati (STESSA composizione di renderWarehouse v1) ----------------

// Header: conteggi del sottotitolo (v1:11607).
export function readWhCounts(win) {
  return {
    stockLines: win.store.warehouseStock().length,
    movements: win.store.listWarehouseMovements().length,
  };
}

// Tab "Giacenze (live)" (v1:11623-11632): stock → righe con prodotto/foto/
// categoria/unità. Rank v1 per colonna (number su qty, date su lastAt via
// sortKey); ordine di consegna = default v1 (_name asc, stabile).
export const WH_LIVE_SORT_COLS = [
  { key: '_img' },
  { key: '_name' },
  { key: 'location' },
  { key: 'qty', type: 'number' },
  { key: 'lastAt', sortKey: (r) => r.lastAt, type: 'date' },
];
export function readWhLive(win) {
  const store = win.store;
  const rows = store.warehouseStock().map((s) => {
    const p = store.getProduct(s.productId);
    const img = whProdImg(p);
    return { ...s, _name: p ? p.name : WH_TEXTS.liveDeleted, _img: img ? img.dataUrl : '', _cat: p ? p.category : '', _units: whProdUnits(p) };
  });
  assignV1SortRanks(rows, WH_LIVE_SORT_COLS);
  return { rows: rows.slice().sort((a, b) => a._v1Rank._name - b._v1Rank._name) };
}
// Cella Giacenza (v1:11630): quantità arrotondata + unità (+ equivalente alt).
export function whLiveQtyParts(r) {
  const q = Math.round(r.qty * 100) / 100;
  const alt = (r._units.altUnit && r._units.altFactor)
    ? '(≈ ' + (Math.round(r.qty * r._units.altFactor * 100) / 100) + ' ' + r._units.altUnit + ')'
    : '';
  return { q, unit: r._units.unit, alt, negative: r.qty < 0 };
}
export function whLiveNameFilterText(r) { return String(r._name || '') + String(r._cat || ''); }
export function whLiveLocationFilterText(r) { return r.location ? r.location : WH_TEXTS.liveNotAllocated; }
export function whLiveQtyFilterText(r) {
  const p = whLiveQtyParts(r);
  return p.q + ' ' + p.unit + (p.alt ? ' ' + p.alt : '');
}
export function whLiveLastFilterText(win, r) { return whPrettyAgo(win, r.lastAt); }

// Tab "Entrate / Uscite" (v1:11633-11687): movimenti → righe con prodotto/
// foto/unità. Rank v1 per colonna (date su at, number su qty; _nphotos SENZA
// type nel v1 ⇒ confronto STRINGA del numero — quirk conservato); consegna =
// default v1 (at desc = asc stabile + reverse, pari merito inclusi).
export const WH_LOG_SORT_COLS = [
  { key: 'at', sortKey: (r) => r.at, type: 'date' },
  { key: 'direction' },
  { key: '_name' },
  { key: 'qty', type: 'number' },
  { key: 'location' },
  { key: 'destination' },
  { key: 'source' },
  { key: 'batchCode' },
  { key: 'notes' },
  { key: '_nphotos' },
  { key: '_del' },
];
export function readWhLog(win) {
  const store = win.store;
  const rows = store.listWarehouseMovements().map((m, i) => {
    const p = store.getProduct(m.productId);
    const img = whProdImg(p);
    // _baseIdx = posizione nell'ordine dello store (at desc): è l'ordine che
    // il v1 usa DIRETTAMENTE nella resa raggruppata (v1:11641-11648, che NON
    // passa da makeDataTable) — groupWhLog riparte da qui.
    return { ...m, _baseIdx: i, _name: p ? p.name : WH_TEXTS.logDeleted, _img: img ? img.dataUrl : '', _nphotos: (m.photos || []).length, _unit: whProdUnits(p).unit };
  });
  assignV1SortRanks(rows, WH_LOG_SORT_COLS);
  return { rows: rows.slice().sort((a, b) => b._v1Rank.at - a._v1Rank.at) };
}
// Cella Q.tà del log (v1:11642 _qtyCell): base = qty arrotondata + unità;
// con enteredUnit/enteredQty mostra 'inserito → convertito'.
export function whLogQtyParts(r) {
  const base = (Math.round(r.qty * 100) / 100) + ' ' + String(r._unit == null ? '' : r._unit);
  const entered = (r.enteredUnit && r.enteredQty != null)
    ? (Math.round(r.enteredQty * 100) / 100) + ' ' + r.enteredUnit
    : null;
  return { base, entered };
}
export function whLogDateFilterText(r) { return whPrettyDate(r.at).slice(0, 16); }
export function whLogDirectionFilterText(r) { return r.direction === 'in' ? WH_TEXTS.logIn : WH_TEXTS.logOut; }
export function whLogNameFilterText(r) { return String(r._name || ''); }
export function whLogQtyFilterText(r) {
  const p = whLogQtyParts(r);
  return p.entered ? p.entered + ' → ' + p.base : p.base;
}
export function whLogLocationFilterText(r) { return r.location ? r.location : '—'; }
export function whLogDestFilterText(r) { return r.destination ? r.destination : '—'; }
export function whLogSourceFilterText(r) { return r.source ? r.source : '—'; }
export function whLogCodeFilterText(r) { return r.batchCode ? r.batchCode : ''; }
export function whLogNotesFilterText(r) { return r.notes ? r.notes : ''; }

// Log RAGGRUPPATO (v1:11643-11669): gruppi per batchCode ('__nogroup__' i
// senza codice), ordinati per ultimo movimento desc (STESSO comparatore v1,
// `<` sui timestamp ISO), badge ENTRATA/USCITA/MISTO, prima destinazione e
// prima fonte non vuote del gruppo. Il v1 raggruppa le righe nell'ORDINE
// DELLO STORE (non quello del makeDataTable): si riparte da _baseIdx.
export function groupWhLog(deliveredRows) {
  const rows = deliveredRows.slice().sort((a, b) => (a._baseIdx || 0) - (b._baseIdx || 0));
  const groups = {};
  rows.forEach((r) => { const k = r.batchCode || '__nogroup__'; (groups[k] = groups[k] || []).push(r); });
  return Object.entries(groups)
    .map(([k, items]) => ({ k, items, last: items.reduce((mx, x) => (x.at > mx ? x.at : mx), '') }))
    .sort((a, b) => (a.last < b.last ? 1 : -1))
    .map((g) => {
      const isNo = g.k === '__nogroup__';
      const dirs = new Set(g.items.map((x) => x.direction));
      const type = dirs.size === 1 ? (dirs.has('in') ? 'in' : 'out') : 'mixed';
      return {
        ...g,
        isNo,
        type,
        dest: g.items.map((x) => x.destination).filter(Boolean)[0],
        src: g.items.map((x) => x.source).filter(Boolean)[0],
      };
    });
}

// Tab "Ricevimenti ordini" (v1:11722-11749): ordini pendenti (confirmed/
// in_transit/partial) + ultime 10 ricevute. Rank v1 per colonna; consegna =
// default v1 (Atteso asc per i pendenti, Data desc per le ricevute).
export const WH_PENDING_STATUSES = ['confirmed', 'in_transit', 'partial'];
export const WH_PENDING_SORT_COLS = [
  { key: 'code' },
  { key: '_supplierName' },
  { key: '_lines', type: 'number' },
  { key: 'status' },
  { key: 'expectedDate', type: 'date' },
  { key: '_act' },
];
export const WH_RECENT_SORT_COLS = [
  { key: 'receivedAt', type: 'date' },
  { key: '_po' },
  { key: '_supplier' },
  { key: 'ddtNumber' },
  { key: '_itemCount', type: 'number' },
  { key: '_hasPhoto' },
];
export function readWhOrders(win) {
  const store = win.store;
  const pending = store.listOrders().filter((o) => WH_PENDING_STATUSES.includes(o.status));
  const pendingRows = pending.map((o) => {
    const sup = store.getSupplier(o.supplierId);
    return { ...o, _supplierName: (sup && sup.name) || '?', _lines: (o.items || []).length };
  });
  assignV1SortRanks(pendingRows, WH_PENDING_SORT_COLS);
  const recent = store.listReceipts({}).slice(0, 10);
  const recentRows = recent.map((r) => {
    const o = store.getOrder(r.orderId);
    const sup = o ? store.getSupplier(o.supplierId) : null;
    return { ...r, _po: (o && o.code) || '?', _supplier: (sup && sup.name) || '?', _hasPhoto: !!r.photoDataUrl, _itemCount: (r.items || []).length };
  });
  assignV1SortRanks(recentRows, WH_RECENT_SORT_COLS);
  return {
    pendingRows: pendingRows.slice().sort((a, b) => a._v1Rank.expectedDate - b._v1Rank.expectedDate),
    recentRows: recentRows.slice().sort((a, b) => b._v1Rank.receivedAt - a._v1Rank.receivedAt),
  };
}
export function whPendingExpectedFilterText(r) { return r.expectedDate ? whPrettyDate(r.expectedDate).slice(0, 10) : '—'; }
export function whPendingStatusFilterText(r) { return ordStatusLabel(r.status); }
export function whRecentDateFilterText(r) { return whPrettyDate(r.receivedAt).slice(0, 16); }

// Tab "Aree" (v1:11688-11717): TUTTE le aree mai usate (anche vuote), con le
// giacenze live per area (quantità ARROTONDATE e poi filtrate > 1e-9, quirk
// v1:11694 conservato) e il totale = somma delle arrotondate.
export function readWhAreas(win) {
  const store = win.store;
  const stock = store.warehouseStock();
  const byArea = {};
  stock.forEach((s) => { const k = s.location || ''; (byArea[k] = byArea[k] || []).push(s); });
  return store.warehouseAreas().map((a) => {
    const items = (byArea[a] || []).map((s) => {
      const p = store.getProduct(s.productId);
      const u = whProdUnits(p);
      return { name: p ? p.name : WH_TEXTS.logDeleted, qty: Math.round(s.qty * 100) / 100, unit: u.unit };
    }).filter((x) => Math.abs(x.qty) > 1e-9);
    const tot = items.reduce((n, x) => n + x.qty, 0);
    return { area: a, items, tot: Math.round(tot * 100) / 100 };
  });
}

// --- Deleghe / azioni al v1 (payload identici: confirmWrite del monolite) ----
export function whOpenIn(win) { win.openWarehouseMovementModal('in'); }
export function whOpenOut(win) { win.openWarehouseMovementModal('out'); }
export function whOpenTransfer(win) { win.openWarehouseTransferModal(); }
export function whOpenReceive(win, orderId) { win.openReceiveModal(orderId); }
export function whOpenOrderDetail(win, orderId) {
  win.currentOrderId = orderId;        // pattern v1 (proc-v0.js:11748)
  win.showView('order-detail');        // il dettaglio RESTA v1 (confine dichiarato)
}
export function whOpenMovementPhotos(win, movement) {
  if (movement && movement.photos && movement.photos[0]) win._openPhotoLightbox(movement.photos[0].dataUrl);
}
export function whOpenReceiptPhoto(win, dataUrl) { win._openPhotoLightbox(dataUrl); }
// Elimina movimento (v1:11687, F1T5 confermato): confirm → DELETE confermato
// → redraw SOLO su r.ok (win.renderWarehouse: sotto flag è il wrapper v3).
export async function whDeleteMovement(win, id) {
  if (!win.confirm(WH_TEXTS.deleteConfirm)) return;
  const r = await win.confirmWrite.deleteWarehouseMovement(id);
  if (r.ok) win.renderWarehouse();
}
// Rinomina area (v1:11707-11717): prompt → store locale → sync API best-effort
// (toast 'warn' onesto se non sincronizzata) → toast successo → redraw.
export async function whRenameArea(win, from) {
  const raw = win.prompt(WH_TEXTS.renamePrompt, from);
  if (raw === null) return;
  const to = raw.trim();
  if (!to || to === from) return;
  win.store.renameWarehouseArea(from, to);
  if (win.isApiMode() && win.api && win.api.warehouse && win.api.warehouse.renameArea) {
    try { await win.api.warehouse.renameArea(from || '', to); }
    catch (e) { win.toast(WH_TEXTS.renameKoPrefix + e.message, 'warn'); }
  }
  win.toast(WH_TEXTS.renameOk, 'success');
  win.renderWarehouse();
}

// Globali/metodi delegati dalla vista Magazzino (guardie install + test
// statico). NB window.WarehouseDDT NON è una guardia: può caricarsi DOPO il
// bundle (script separato) e il v1 stesso ha il fallback a runtime — il v3
// replica il fallback, non blocca l'install.
export const WH_DELEGATED_GLOBALS = [
  'showView', 'toast', 'isApiMode',
  'openWarehouseMovementModal', 'openWarehouseTransferModal', 'openReceiveModal', '_openPhotoLightbox',
];
export const WH_DELEGATED_METHODS = {
  confirmWrite: ['deleteWarehouseMovement'],
  store: [
    'warehouseStock', 'listWarehouseMovements', 'warehouseAreas', 'renameWarehouseArea',
    'listOrders', 'listReceipts', 'getProduct', 'getOrder', 'getSupplier',
  ],
};

// ============================================================================
// F2.3g — vista "Monitoraggio" (renderMonitoring v1, proc-v0.js:12023-12137)
// ============================================================================
// MANDATO ondata 22: le due CARD-GRID del monolite scartate dall'ondata 21
// perché non tabellari — ora sul SECONDO componente condiviso (CardGrid).
// La vista v1 non ha stato proprio (niente filtri/tab/binding): ogni card
// viene composta dallo store a ogni render. Le azioni modali complesse
// ("Esegui ora" → _confirmRunSchedule, "Cronologia" → _showScheduleHistory,
// "+ Nuova/Modifica" → openScheduleModal) si DELEGANO alle globali v1;
// toggle Pausa/Riprendi ed Elimina replicano il wire inline del v1 con le
// STESSE primitive confirmWrite (payload identici per costruzione).

// LABEL dal dizionario I18N v1 (VERBATIM): la vista è TUTTA hardcoded in
// italiano (quirk conservato, stesso genere della segnalazione S1 f23f) —
// l'unica chiave dict del perimetro replicato è il toast dell'Elimina.
export const MON_LABELS = {
  en: { 'toast.deleted': 'Deleted' },
  it: { 'toast.deleted': 'Eliminato' },
};

// Testi LETTERALI del v1 (hardcoded in renderMonitoring, in ENTRAMBE le lingue).
export const MON_TEXTS = {
  title: 'Monitoraggio di mercato',
  subtitle: 'RFQ ricorrenti per monitorare nel tempo i prezzi di mercato/concorrenti per lo stesso prodotto. Ogni ciclo ruota l\'identità del mittente affinché i fornitori non capiscano che è sempre lo stesso acquirente.',
  newBtn: '+ Nuova pianificazione',
  bannerTail: ' in scadenza o scadute.',
  bannerSpan: 'Clicca "Esegui ora" su ciascuna per avviare il prossimo ciclo RFQ con un\'identità ruotata.',
  emptyMessage: 'Nessuna pianificazione di monitoraggio. Clicca + Nuova pianificazione.',
  untitled: 'Senza titolo',
  categoryPrefix: 'Categoria: ',
  productPrefix: 'Prodotto: ',
  productMissing: 'Product missing',
  badgeCategory: 'Category',
  badgeProduct: 'Product',
  cadenceMid: ' · ogni ',
  cadenceTail: ' mesi',
  editBtn: 'Modifica',
  nextCycleH: 'Prossimo ciclo',
  lastCycleH: 'Ultimo ciclo',
  noDue: 'nessuna scadenza',
  overduePrefix: 'in ritardo di ',
  dueToday: 'scade oggi',
  duePrefix: 'tra ',
  daysSuffix: 'g',
  neverRun: 'mai eseguito',
  viaPrefix: 'via ',
  rotationPrefix: 'Rotazione identità (',
  nextMarker: ' ← next',
  noIdentities: 'Nessuna identità configurata',
  suppliersWord: ' fornitori',
  contactedText: ' saranno contattati: ',
  morePrefix: ', +',
  moreSuffix: ' altri',
  runBtn: 'Esegui ora',
  pauseBtn: 'Pausa',
  resumeBtn: 'Riprendi',
  historyPrefix: 'Cronologia (',
  deleteBtn: 'Elimina',
  statusActive: 'active',
  statusPaused: 'paused',
  deleteConfirm: 'Eliminare questa pianificazione di monitoraggio?',
};

export function tMon(win, key) {
  const lang = pickLang(win);
  const d = MON_LABELS[lang] || MON_LABELS.it;
  return d[key] != null ? d[key] : (MON_LABELS.it[key] != null ? MON_LABELS.it[key] : key);
}

// Banner ambra (v1:12037-12040): singolare/plurale come il v1
// (`pianificazion${n===1?'e':'i'}`).
export function monDueBanner(n) {
  return n + ' pianificazion' + (n === 1 ? 'e' : 'i') + MON_TEXTS.bannerTail;
}

// Scadenza relativa (v1:12062-12066): floor dei GIORNI da NOW() — NOW è una
// const lessicale del monolite (() => new Date(), v1:10): qui new Date().
export function monDueParts(nextDueAt) {
  if (!nextDueAt) return { kind: 'none', days: null };
  const days = Math.floor((new Date() - new Date(nextDueAt)) / (24 * 60 * 60 * 1000));
  if (days > 0) return { kind: 'overdue', days };
  if (days === 0) return { kind: 'today', days: 0 };
  return { kind: 'future', days: -days };
}
export function monDueLabel(parts) {
  if (parts.kind === 'none') return MON_TEXTS.noDue;
  if (parts.kind === 'overdue') return MON_TEXTS.overduePrefix + parts.days + MON_TEXTS.daysSuffix;
  if (parts.kind === 'today') return MON_TEXTS.dueToday;
  return MON_TEXTS.duePrefix + parts.days + MON_TEXTS.daysSuffix;
}

// Lettura dati: STESSA composizione di renderMonitoring v1 (proc-v0.js:
// 12024-12122): ordine = store.listSchedules() (ordine DB.monitoringSchedules,
// cioè l'ORDER BY next_due_at della GET — nessun sort client), banner =
// listDueSchedules().length, per-card prodotto/scadenze/identità/fornitori.
// NB pickNextIdentity è chiamata A OGNI RENDER come nel v1 (con strategy
// 'random' il chip evidenziato può cambiare tra i render: quirk v1 conservato).
export function readMonitoring(win) {
  const store = win.store;
  const rows = store.listSchedules().map((s) => {
    const isCategory = s.targetKind === 'category';
    const product = !isCategory ? store.getProduct(s.productId) : null;
    const nextIdId = store.pickNextIdentity(s);
    const lastId = s.lastUsedIdentityId ? store.getIdentity(s.lastUsedIdentityId) : null;
    return {
      ...s,
      _isCategory: isCategory,
      _productName: product ? product.name : null,
      _due: monDueParts(s.nextDueAt),
      _nextDueDate: s.nextDueAt ? whPrettyDate(s.nextDueAt).slice(0, 10) : '—',
      _lastRunDate: s.lastRunAt ? whPrettyDate(s.lastRunAt).slice(0, 10) : null,
      _lastIdName: s.lastUsedIdentityId ? ((lastId && lastId.name) || s.lastUsedIdentityId) : null,
      _identities: (s.identityIds || []).map((iid) => {
        const id = store.getIdentity(iid);
        return { iid, name: (id && id.name) || iid, email: (id && id.email) || '', isNext: iid === nextIdId };
      }),
      _supCount: (s.supplierIds || []).length,
      _supNames: (s.supplierIds || []).slice(0, 3).map((sid) => {
        const sup = store.getSupplier(sid);
        return (sup && sup.name) || sid;
      }),
      _historyCount: (s.history || []).length,
    };
  });
  return { rows, dueCount: store.listDueSchedules().length };
}

// --- Deleghe / azioni al v1 (payload identici: confirmWrite del monolite) ----
export function monOpenNew(win) { win.openScheduleModal(); }
export function monOpenEdit(win, id) { win.openScheduleModal(id); }
export function monOpenProduct(win, id) {
  win.currentProductId = id;           // pattern v1 (proc-v0.js:12126)
  win.showView('product-detail');      // il dettaglio RESTA v1 (confine dichiarato)
}
export function monRun(win, id) { win._confirmRunSchedule(id); }       // MODALE v1 (delega)
export function monHistory(win, id) { win._showScheduleHistory(id); } // MODALE v1 (delega)
// Pausa/Riprendi (v1:12127-12133, F1T5): PATCH CONFERMATO — su errore (anche
// offline) lo stato in UI NON cambia; redraw con rerenderAll come il v1.
// NB parità: come il v1, NESSUNA guardia su getSchedule (l'id viene dalla card).
export async function monToggleStatus(win, id) {
  const s = win.store.getSchedule(id);
  const r = await win.confirmWrite.schedule(id, { status: s.status === 'active' ? 'paused' : 'active' });
  if (!r.ok) return;
  win.rerenderAll();
}
// Elimina (v1:12136, F1T5 confermato): confirm → DELETE confermato → toast dal
// dict + rerenderAll SOLO su r.ok (il toast d'errore lo fa confirmWrite).
export async function monDelete(win, id) {
  if (!win.confirm(MON_TEXTS.deleteConfirm)) return;
  const r = await win.confirmWrite.deleteSchedule(id);
  if (!r.ok) return;
  win.toast(tMon(win, 'toast.deleted'), 'success');
  win.rerenderAll();
}

// Globali/metodi delegati dalla vista Monitoraggio (guardie install + test
// statico). confirmWrite e store sono OGGETTI su window: guardia sui metodi.
export const MON_DELEGATED_GLOBALS = [
  'showView', 'toast', 'rerenderAll',
  'openScheduleModal', '_confirmRunSchedule', '_showScheduleHistory',
];
export const MON_DELEGATED_METHODS = {
  confirmWrite: ['schedule', 'deleteSchedule'],
  store: [
    'listSchedules', 'listDueSchedules', 'getSchedule', 'pickNextIdentity',
    'getIdentity', 'getProduct', 'getSupplier',
  ],
};

// ============================================================================
// F2.3g — vista "Templates" / Libreria modelli (renderTemplates v1,
// proc-v0.js:10323-10367)
// ============================================================================
// Lo stato di vista del v1 (templateSearch, templateCollapsed) è fatto di
// binding LESSICALI di modulo (v1:10324-10325): sopravvive alla navigazione
// tra viste e muore al reload. Il v3 replica ESATTAMENTE quel ciclo di vita
// tenendo lo stato nella CLOSURE della factory (createProcV3Templates) e
// passandolo al componente: unmount/remount non lo azzera, il reload sì.

// Testi LETTERALI del v1 (la vista è TUTTA hardcoded, zero chiavi dict).
export const TPL_TEXTS = {
  title: 'Libreria modelli',
  searchPlaceholder: 'Cerca modelli…',
  newBtn: '+ Nuovo modello',
  emptyMessage: 'Nessun modello corrisponde.',
  noGroupKey: '— Senza gruppo —',
  noGroupLabel: 'Senza gruppo',
  collapsedToggle: '▸ mostra',
  expandedToggle: '▾ nascondi',
};

// _tplGroup v1 (proc-v0.js:10328) VERBATIM.
export function tplGroupOf(t) { return (t.group || t.group_name || '').trim(); }

// Badge della card (_tplCard v1:10331): '{kind} · {language||lang} · {channel}'.
// NB 'kind' non ha colonna server (hydration f23g-studio): sui dati reali il
// primo segmento è vuoto — quirk v1 conservato.
export function tplBadgeText(t) {
  return String(t.kind || '') + ' · ' + String(t.language || t.lang || '') + ' · ' + String(t.channel || '');
}

// Composizione: STESSA di renderTemplates v1 (proc-v0.js:10338-10343):
// filtro substring case-insensitive su [name, subject, body, gruppo]
// (filter(Boolean): i vuoti non matchano mai), gruppi con sentinella
// '— Senza gruppo —', ordine nomi = localeCompare con la sentinella ULTIMA.
// L'ordine delle card dentro il gruppo è l'ordine dello store (GET /templates
// ORDER BY COALESCE(group_name,''), name — nessun sort client).
export function readTemplates(win, search) {
  const all = win.store.listTemplates();
  const q = String(search || '').toLowerCase().trim();
  const list = q
    ? all.filter((t) => [t.name, t.subject, t.body, tplGroupOf(t)].filter(Boolean).some((x) => String(x).toLowerCase().includes(q)))
    : all;
  const groups = {};
  list.forEach((t) => { const g = tplGroupOf(t) || TPL_TEXTS.noGroupKey; (groups[g] = groups[g] || []).push(t); });
  const names = Object.keys(groups).sort((a, b) => (a === TPL_TEXTS.noGroupKey ? 1 : b === TPL_TEXTS.noGroupKey ? -1 : a.localeCompare(b)));
  return names.map((g) => ({ name: g, isNoGroup: g === TPL_TEXTS.noGroupKey, items: groups[g] }));
}

// --- Deleghe al v1 (il modale del template RESTA v1) --------------------------
export function tplOpenNew(win) { win.openTemplateModal(null); }
// Click card → MODALE v1 (come Conversazioni: il v3 resta montato sotto;
// salva/elimina del modale chiudono con rerenderAll → wrapper v3).
export function tplOpenDetail(win, id) { win.openTemplateModal(id); }

export const TPL_DELEGATED_GLOBALS = ['openTemplateModal'];
export const TPL_DELEGATED_METHODS = { store: ['listTemplates'] };

// ============================================================================
// F2.3h — vista "Categorie" (renderCategories v1, proc-v0.js:8092-8963)
// ============================================================================
// La vista più complessa del monolite: albero tassonomia (pannello sinistro,
// vista Albero/Lista con ricerca token-AND su path+name+keywords) + pannello
// dettaglio a 4 tab (suppliers/products/quotations/settings). Lo STATO DI
// VISTA del v1 è fatto di binding LESSICALI (catSelected/catTab/catSearch/
// catSearchPrev/catExpanded/catViewMode, v1:8059-8078) → closure della
// factory (createProcV3Categories), stesso ciclo di vita (sopravvive alla
// navigazione, muore al reload). I TRE Set di ricerca e la clipboard sono
// invece GLOBALI su window nel v1 (catSearchManualExpanded/catSearchCollapsed/
// catSearchRevealed/_catClipboard, v1:8074-8076/8567) → il v3 usa GLI STESSI
// (interop v1↔v3 anche cambiando flag a metà lavoro).
//
// CONFINE DICHIARATO (mandato f23h): il DRAG&DROP (categoria→categoria,
// prodotto→categoria) resta SOLO nel v1 — HTML5 dnd con stato di
// trascinamento module-level, replicabile ma non in modo pulito né
// collaudabile in modo affidabile. La STESSA operazione resta nel v3 via
// Taglia/Incolla del menu contestuale (stessa primitiva
// confirmWrite.moveCategory ⇒ payload identico) e via modale prodotto.

// LABEL dal dizionario I18N v1 (VERBATIM, test di sync): la vista usa il dict
// SOLO per titolo, toast e badge affidabilità — tutto il resto è hardcoded IT
// (quirk conservato, v. S1 f23f/f23g).
export const CAT_LABELS = {
  en: {
    'cat.title': 'Categories',
    'toast.added': 'Added',
    'toast.deleted': 'Deleted',
    'toast.already_exists': 'Already exists',
    'badge.high': 'High',
    'badge.medium': 'Medium',
    'badge.low': 'Low',
  },
  it: {
    'cat.title': 'Categorie',
    'toast.added': 'Aggiunto',
    'toast.deleted': 'Eliminato',
    'toast.already_exists': 'Esiste già',
    'badge.high': 'Alta',
    'badge.medium': 'Media',
    'badge.low': 'Bassa',
  },
};

export function catLabel(win, key) {
  const lang = pickLang(win);
  const d = CAT_LABELS[lang] || CAT_LABELS.it;
  return d[key] != null ? d[key] : (CAT_LABELS.it[key] != null ? CAT_LABELS.it[key] : key);
}

// SENTINEL_CAT del v1 (proc-v0.js:3496) — const LESSICALE, non raggiungibile
// dal bundle: pinnata qui, il test statico rilegge il monolite.
export const CAT_SENTINEL = '_da-categorizzare';

// Testi LETTERALI del v1 (hardcoded IT in renderCategories/_renderCatTree/
// _renderCatList/_renderCatDetail, fuori dal dict) — stessi byte, pinnati.
export const CAT_TEXTS = {
  subtitle: 'Albero gerarchico dei prodotti. I prodotti si trovano nelle foglie. I fornitori possono appartenere a qualsiasi nodo.',
  btnAi: 'Aggiungi categoria (AI)',
  btnImport: 'Importa prodotti (AI)',
  btnRoot: '+ Aggiungi radice',
  searchPlaceholder: "Cerca nell'albero o tra i prodotti…",
  viewTree: 'Albero',
  viewTreeTitle: 'Vista ad albero',
  viewList: 'Lista',
  viewListTitle: 'Vista a lista',
  expandAll: '⊞',
  expandAllTitle: 'Espandi tutto',
  collapseAll: '⊟',
  collapseAllTitle: 'Comprimi tutto',
  emptyTree: 'Nessuna categoria.',
  emptyList: 'Nessun risultato.',
  productMarker: '◆ prodotto',
  addSubTitle: 'Aggiungi sottocategoria',
  addProdTitle: 'Aggiungi prodotto in questa categoria',
  renameTitle: 'Rinomina',
  deleteTitle: 'Elimina',
  ctxExpandSub: 'Espandi tutto qui sotto',
  ctxCollapseSub: 'Comprimi tutto qui sotto',
  ctxOnlyHere: 'Comprimi tutto tranne questo',
  ctxAdd: 'Aggiungi sottocategoria',
  ctxRename: 'Rinomina',
  ctxDelete: 'Elimina',
  ctxCut: 'Taglia',
  ctxPaste: 'Incolla qui',
  noneSelected: 'Nessuna categoria selezionata.',
  notFound: 'Categoria non trovata.',
  selectedLabel: 'Categoria selezionata',
  leafBadge: 'foglia',
  branchBadge: 'ramo',
  supDesc: 'Fornitori che gestiscono questo ramo (corrispondenza diretta + tramite discendenti).',
  supPickPlaceholder: 'Aggiungi fornitore esistente (cerca)…',
  supNewBtn: '+ Nuovo (form)',
  supEmpty: 'Nessun fornitore qualificato in questo ramo.',
  supDirect: 'direct',
  supViaDesc: 'tramite discendente',
  supAddTag: '+ tag',
  prodAddBtn: '+ Aggiungi prodotto a questa categoria',
  prodAiBtn: '✨ Categorizza con AI',
  prodEmpty: 'Nessun prodotto in questo ramo.',
  quotEmpty: 'Nessun preventivo in questo ramo.',
  setPath: 'Path',
  setTipo: 'Tipo',
  setLeafLong: 'Foglia — i prodotti possono risiedere qui',
  setBranchLong: 'Ramo — ha sottocategorie, nessun prodotto diretto',
  setRename: 'Rinomina',
  setAdd: 'Aggiungi sottocategoria',
  setDelete: 'Elimina',
  kwLabel: 'Keyword AI',
  kwLabelSub: '(usate per la ricerca e per trovare i fornitori adatti)',
  kwEmpty: 'Nessuna keyword AI per questa categoria. Clicca "Genera" per crearle.',
  kwChipTitle: 'Click per eliminare',
  kwGenerate: 'Genera keyword AI',
  kwRegenerate: 'Rigenera keyword AI (si aggiungono alle esistenti)',
  kwGenerating: 'Generazione in corso…',
  kwCalling: "L'AI interna sta generando le parole chiave: di solito meno di un minuto.",
  kwRetry: 'Riprova',
  kwManualLabel: 'Aggiungi keyword manuale',
  kwManualPlaceholder: 'es. pdc, pompa di calore, brand X...',
  kwManualAdd: 'Aggiungi',
  kwManualHint: "Le keyword manuali si aggiungono a quelle dell'AI. Per eliminarne una, premi la sua × .",
};

// ---------------------------------------------------------------------------
// Logica PURA dell'albero (parità _renderCatTree v1:8257-8330)
// ---------------------------------------------------------------------------

// Token della ricerca (v1:8260): lowercase, split su whitespace, senza vuoti.
export function catTokens(search) {
  return String(search || '').toLowerCase().split(/\s+/).filter(Boolean);
}

// Haystack del match categoria (v9, v1:8266): path + name + keywords AI.
function catHay(cat) {
  return (cat.path + ' ' + (cat.name || '') + ' ' + (cat.keywords || []).join(' ')).toLowerCase();
}

// Set dei path che matchano TUTTI i token (AND, v1:8262-8273).
export function catMatchSet(categories, tokens) {
  const matches = new Set();
  if (!tokens.length) return matches;
  for (const c of categories || []) {
    const hay = catHay(c);
    if (tokens.every((t) => hay.includes(t))) matches.add(c.path);
  }
  return matches;
}

// effectiveExpanded (v16, v1:8277-8292): senza token = catExpanded; con token
// = antenati di ogni match + espansioni manuali − collassi espliciti.
export function catEffectiveExpanded({ tokens, matches, baseExpanded, manualExpanded, collapsed }) {
  if (!tokens.length) return baseExpanded;
  const eff = new Set();
  matches.forEach((m) => {
    const parts = m.split('/');
    for (let i = 1; i < parts.length; i++) eff.add(parts.slice(0, i).join('/'));
  });
  if (manualExpanded) manualExpanded.forEach((p) => eff.add(p));
  if (collapsed) collapsed.forEach((p) => eff.delete(p));
  return eff;
}

// Genitori dei match (v1:8297-8298): quel ramo si apre INTERO durante la search.
export function catMatchParents(matches) {
  const parents = new Set();
  matches.forEach((m) => { const i = m.lastIndexOf('/'); if (i > 0) parents.add(m.slice(0, i)); });
  return parents;
}

// _showsAllChildren (v1:8299).
export function catShowsAllChildren(path, { manualExpanded, revealed, matchParents, collapsed }) {
  return (manualExpanded && manualExpanded.has(path))
    || (revealed && revealed.has(path))
    || ((matchParents.has(path)) && !(collapsed && collapsed.has(path)));
}

// Contesto di resa dell'albero: TUTTO ciò che serve alla vista, calcolato UNA
// volta per render (composizione pura, la parte più testata della vista).
export function catTreeContext(win, viewState) {
  const categories = (win.DB && win.DB.categories) || [];
  const tokens = catTokens(viewState.search);
  const matches = catMatchSet(categories, tokens);
  const effectiveExpanded = catEffectiveExpanded({
    tokens, matches,
    baseExpanded: viewState.expanded,
    manualExpanded: win.catSearchManualExpanded,
    collapsed: win.catSearchCollapsed,
  });
  const matchParents = catMatchParents(matches);
  const subtreeHasMatch = (n) => matches.has(n.path) || n.children.some((c) => subtreeHasMatch(c));
  const showsAll = (path) => catShowsAllChildren(path, {
    manualExpanded: win.catSearchManualExpanded,
    revealed: win.catSearchRevealed,
    matchParents,
    collapsed: win.catSearchCollapsed,
  });
  // visibleNode (v1:8300-8308) — per le RADICI il genitore è '' e vale il caso
  // speciale; per i figli il walk del TreeView garantisce già il genitore
  // aperto, ma il predicato resta INTERO (stessa formula del v1).
  const visibleNode = (n) => {
    if (!tokens.length) return true;
    const parentPath = n.path.includes('/') ? n.path.slice(0, n.path.lastIndexOf('/')) : '';
    if (!parentPath) {
      return matches.has(n.path) || effectiveExpanded.has(n.path)
        || n.children.some((c) => subtreeHasMatch(c))
        || (win.catSearchManualExpanded && win.catSearchManualExpanded.has(n.path));
    }
    if (!effectiveExpanded.has(parentPath)) return false;
    return subtreeHasMatch(n) || showsAll(parentPath);
  };
  // _hiddenChildrenCount (v1:8309-8312) → riga "⋯ altre N categorie".
  const hiddenChildrenCount = (node) => {
    if (!tokens.length || showsAll(node.path)) return 0;
    return node.children.filter((c) => !subtreeHasMatch(c)).length;
  };
  return { categories, tokens, matches, effectiveExpanded, matchParents, subtreeHasMatch, showsAll, visibleNode, hiddenChildrenCount };
}

// productMatches della vista ALBERO (v1:8313-8317): name+category, SENZA brand.
export function catProductMatches(p, tokens) {
  if (!tokens.length) return false;
  const composite = (p.name + ' ' + p.category).toLowerCase();
  return tokens.every((t) => composite.includes(t));
}

// Prodotti da mostrare sotto un nodo (v1:8344-8346): solo foglie, se aperte o
// selezionate o in ricerca; in ricerca filtrati su match prodotto O match nodo.
export function catNodeProducts(win, node, { isOpen, isSelected, tokens, matches }) {
  const hasKids = node.children.length > 0;
  if (hasKids || !(isOpen || isSelected || tokens.length)) return [];
  const products = ((win.DB && win.DB.products) || []).filter((p) => p.category === node.path);
  return products.filter((p) => !tokens.length || catProductMatches(p, tokens) || matches.has(node.path));
}

// Conteggi della riga nodo (v1:8338-8342).
export function catNodeCounts(win, path) {
  const supCount = win.store.listSuppliers({ category: path, excludeKind: 'competitor' }).length;
  const products = (win.DB && win.DB.products) || [];
  const subtreeProductCount = products.filter((p) => p.category === path || p.category.startsWith(path + '/')).length;
  return { supCount, subtreeProductCount };
}

// Badge prodotti: title singolare/plurale (v1:8353).
export function catProdBadgeTitle(n) {
  return n + ' prodotto' + (n === 1 ? '' : 'i') + ' in questo ramo';
}

// Riga "⋯ altre N categorie" (v1:8371).
export function catMoreLabel(hc) {
  return hc === 1 ? 'altra 1 categoria' : 'altre ' + hc + ' categorie';
}

// Toggle UNIFICATO di un nodo (v1:8406-8414): lo stato corrente è quello che
// il render usa davvero (effectiveExpanded). MUTA i Set passati (come il v1).
export function catToggleNode(path, { tokens, effectiveExpanded, manualExpanded, collapsed, baseExpanded }) {
  const isOpen = effectiveExpanded.has(path);
  if (tokens.length) {
    if (isOpen) { manualExpanded.delete(path); collapsed.add(path); }
    else { collapsed.delete(path); manualExpanded.add(path); }
  } else {
    if (isOpen) baseExpanded.delete(path); else baseExpanded.add(path);
  }
}

// _catEnsureExpanded (v1:8080): espande tutti gli ANTENATI del path.
export function catEnsureExpanded(expanded, path) {
  const parts = path.split('/');
  for (let i = 1; i < parts.length; i++) expanded.add(parts.slice(0, i).join('/'));
}

// Preambolo di renderCategories (v1:8095-8096): selezione di default +
// antenati della selezione espansi. Gira SOLO al render del wrapper (vista
// che entra / rerenderAll / toggle vista / catch-up), NON sui repaint interni
// — stessa differenza renderCategories vs _renderCatTree del v1.
export function catRenderPreamble(win, viewState) {
  const categories = (win.DB && win.DB.categories) || [];
  if (!viewState.selected && categories[0]) viewState.selected = categories[0].path;
  if (viewState.selected) catEnsureExpanded(viewState.expanded, viewState.selected);
}

// Reset dei Set di ricerca al CAMBIO search (v13/v16/v17, v1:8148-8155).
// MUTA viewState e i Set globali (gli stessi del v1). Ritorna il valore
// TRIMMATO che il v1 tiene in catSearch.
export function catApplySearchInput(win, viewState, rawValue) {
  const next = String(rawValue == null ? '' : rawValue).trim();
  viewState.search = next;
  if (next !== viewState.searchPrev) {
    win.catSearchManualExpanded.clear();
    win.catSearchCollapsed.clear();
    win.catSearchRevealed.clear();
    viewState.searchPrev = next;
  }
  return next;
}

// ---------------------------------------------------------------------------
// Vista LISTA (parità _renderCatList v1:8180-8245)
// ---------------------------------------------------------------------------

// Entry della lista piatta: categorie (match su path+name+keywords) POI
// prodotti (match su name+category+brand — QUI col brand, quirk v1 conservato).
export function catListEntries(win, search) {
  const tokens = catTokens(search);
  const entries = [];
  const categories = (win.DB && win.DB.categories) || [];
  categories.forEach((c) => {
    const lo = catHay(c);
    if (!tokens.length || tokens.every((t) => lo.includes(t))) {
      entries.push({ kind: 'category', path: c.path, name: c.name || c.path.split('/').pop() });
    }
  });
  const products = (win.DB && win.DB.products) || [];
  products.forEach((p) => {
    const composite = (p.name + ' ' + p.category + ' ' + (p.brand || '')).toLowerCase();
    if (!tokens.length || tokens.every((t) => composite.includes(t))) {
      entries.push({ kind: 'product', path: p.category, name: p.name, brand: p.brand, productId: p.id });
    }
  });
  return { tokens, entries };
}

// Header della lista (v1:8217): 'N risultato/i' (+ ' per "q"' se in ricerca).
export function catListHeaderParts(count, search, tokens) {
  return {
    countText: count + ' ' + (count === 1 ? 'risultato' : 'risultati'),
    query: tokens.length ? search : null,
  };
}

// Conteggi della card categoria in lista (v1:8225-8230).
export function catListCategoryCounts(win, path) {
  const supCount = win.store.listSuppliers({ category: path, excludeKind: 'competitor' }).length;
  const products = (win.DB && win.DB.products) || [];
  const prodCount = products.filter((p) => p.category === path || p.category.startsWith(path + '/')).length;
  return {
    supCount, prodCount,
    text: supCount + ' fornitore' + (supCount === 1 ? '' : 'i') + ' · ' + prodCount + ' prodotto' + (prodCount === 1 ? '' : 'i'),
  };
}

// ---------------------------------------------------------------------------
// Pannello DETTAGLIO (parità _renderCatDetail v1:8658-8963)
// ---------------------------------------------------------------------------

// Fornitori del ramo, DEDUP per id e per nome+paese (v1:8680-8686).
export function catDetailSuppliers(win, path) {
  const seen = new Set();
  return win.store.listSuppliers({ category: path, excludeKind: 'competitor' }).filter((s) => {
    const kId = 'id:' + s.id;
    const kName = 'nm:' + (s.name || '').trim().toLowerCase() + '|' + (s.country || '').trim().toLowerCase();
    if (seen.has(kId) || seen.has(kName)) return false;
    seen.add(kId); seen.add(kName); return true;
  });
}

// Preventivi del ramo: per ogni prodotto del ramo, le sue quotazioni con il
// prodotto agganciato (v1:8688-8689).
export function catDetailQuotations(win, products) {
  const quotations = [];
  (products || []).forEach((p) => win.store.listQuotationsByProduct(p.id).forEach((q) => quotations.push({ ...q, _product: p })));
  return quotations;
}

// Etichetta tab (v1:8700): id inglese con la prima MAIUSCOLA (quirk: anche in it).
// Etichette italiane delle schede (prima: id inglesi maiuscolati, "Suppliers", "Settings"...)
const CAT_TAB_IT = { suppliers: 'Fornitori', products: 'Prodotti', quotations: 'Preventivi', settings: 'Impostazioni' };
export function catTabLabel(tabId) {
  return CAT_TAB_IT[tabId] || (tabId[0].toUpperCase() + tabId.slice(1));
}

// Segmenti del tagBadge v1 (v1:3152-3163) — stessa scelta colore, resa React.
export function catTagBadgeParts(tag) {
  const isScoped = tag.includes(' @ ');
  const [body, scope] = isScoped ? tag.split(' @ ') : [tag, null];
  let color = 'bg-slate-100 text-slate-700';
  if (body === 'type:competitor') color = 'bg-fuchsia-100 text-fuchsia-800';
  else if (body.startsWith('type:')) color = 'bg-purple-100 text-purple-800';
  else if (body.startsWith('brand:')) color = 'bg-cyan-100 text-cyan-800';
  else if (body.startsWith('size:') || body.startsWith('payment:')) color = 'bg-emerald-100 text-emerald-800';
  else color = 'bg-rose-100 text-rose-800';
  return { body, scope, color };
}

// confidenceBadge v1 (v1:3164-3170): colore + label dal dict.
export function catConfidenceParts(win, c) {
  const color = c === 'High' ? 'text-emerald-700' : c === 'Medium' ? 'text-amber-700' : 'text-slate-500';
  const labelKey = c === 'High' ? 'badge.high' : c === 'Medium' ? 'badge.medium' : c === 'Low' ? 'badge.low' : null;
  const label = labelKey ? catLabel(win, labelKey) : (c || '');
  return { color, label };
}

// ---------------------------------------------------------------------------
// AZIONI — stesse primitive del v1 (payload identici per costruzione), stessi
// prompt/confirm/alert/toast BYTE-IDENTICI. Ogni azione ritorna true se ha
// scritto (il chiamante ridisegna), false/undefined se annullata o fallita
// (confirmWrite ha già dichiarato l'errore RUMOROSAMENTE).
// ---------------------------------------------------------------------------

// _catPrompt (v1:8591-8605). MUTA viewState su successo (expand+select).
export async function catAdd(win, viewState, parentPath) {
  const name = win.prompt(parentPath ? `Nuova sottocategoria di "${parentPath}":` : 'Nome nuova categoria radice:', '');
  if (!name) return false;
  const slug = name.toLowerCase().trim().replace(/\s+/g, '-');
  const newPath = parentPath ? `${parentPath}/${slug}` : slug;
  const categories = (win.DB && win.DB.categories) || [];
  if (categories.find((c) => c.path === newPath)) { win.toast(catLabel(win, 'toast.already_exists'), 'error'); return false; }
  const r = await win.confirmWrite.createCategory(newPath);
  if (!r.ok) return false;
  viewState.expanded.add(parentPath || newPath);
  viewState.selected = newPath;
  win.toast(catLabel(win, 'toast.added'), 'success');
  return true;
}

// _catRename (v1:8606-8623). Riallinea la selezione su successo.
export async function catRename(win, viewState, path) {
  const cur = path.split('/').pop();
  const next = win.prompt(`Rinomina "${path}" — nuovo nome ultimo segmento:`, cur);
  if (!next || next === cur) return false;
  const slug = next.toLowerCase().trim().replace(/\s+/g, '-');
  const parent = path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '';
  const newPath = parent ? `${parent}/${slug}` : slug;
  const categories = (win.DB && win.DB.categories) || [];
  if (categories.find((c) => c.path === newPath)) { win.toast('Percorso già esistente', 'error'); return false; }
  const r = await win.confirmWrite.renameCategory(path, newPath);
  if (!r.ok) { if (r.error && typeof r.error === 'string') win.toast(r.error, 'error'); return false; }
  const sel = viewState.selected || '';
  if (sel === path || sel.startsWith(path + '/')) viewState.selected = newPath + sel.slice(path.length);
  win.toast('Rinominata', 'success');
  return true;
}

// _catDelete (v1:8624-8647): blocco INFORMATO se in uso, conferma, DELETE.
export async function catDelete(win, viewState, path) {
  const categories = (win.DB && win.DB.categories) || [];
  const suppliers = (win.DB && win.DB.suppliers) || [];
  const products = (win.DB && win.DB.products) || [];
  const hasChildren = categories.some((c) => c.path.startsWith(path + '/'));
  const supUsage = suppliers.filter((s) => (s.categories || []).some((c) => c === path || c.startsWith(path + '/'))).length;
  const prodUsage = products.filter((p) => p.category === path || p.category.startsWith(path + '/')).length;
  if (hasChildren || supUsage || prodUsage) {
    const msg = `"${path}" è in uso:\n` +
      (hasChildren ? `· ha sottocategorie figlie\n` : '') +
      (supUsage ? `· ${supUsage} fornitore/i lo referenzia\n` : '') +
      (prodUsage ? `· ${prodUsage} prodotto/i risiede sotto di esso\n` : '') +
      `\nEliminazione bloccata — rimuovi prima i riferimenti.`;
    win.alert(msg);
    return false;
  }
  if (!win.confirm(`Eliminare "${path}"? Foglia vuota — sicuro rimuoverla.`)) return false;
  const r = await win.confirmWrite.deleteCategory(path);
  if (!r.ok) return false;
  viewState.expanded.delete(path);
  if (viewState.selected === path) viewState.selected = (categories[0] && categories[0].path) || null;
  win.toast(catLabel(win, 'toast.deleted'), 'success');
  return true;
}

// Incolla del menu contestuale (v1:8569-8585): stessa primitiva del dnd
// (confirmWrite.moveCategory ⇒ payload IDENTICO), stessi toast.
export async function catPaste(win, viewState, targetPath) {
  const src = win._catClipboard;
  if (!src) { win.toast('Niente da incollare: prima usa Taglia su una categoria', 'warn'); return false; }
  if (src === targetPath) { win.toast('Origine e destinazione coincidono', 'warn'); return false; }
  const r = await win.confirmWrite.moveCategory(src, targetPath);
  if (r && r.ok) {
    win._catClipboard = null;
    const sel = viewState.selected || '';
    if (sel === src || sel.startsWith(src + '/')) viewState.selected = targetPath + '/' + src.split('/').pop();
    win.toast('Spostata in "' + targetPath + '"', 'success');
    return true;
  }
  win.toast('Spostamento non riuscito: ' + ((r && r.error) || '?'), 'error');
  return false;
}

// Taglia (v1:8566-8568): clipboard GLOBALE condivisa col v1.
export function catCut(win, path) {
  win._catClipboard = path;
  win.toast('Tagliata "' + path.split('/').pop() + '" — tasto destro › Incolla qui sulla destinazione', 'success');
}

// Azioni del menu contestuale sugli espansi (v1:8552-8562). MUTANO viewState.expanded.
export function catCtxExpandSub(win, viewState, path) {
  const categories = (win.DB && win.DB.categories) || [];
  categories.forEach((c) => { if (c.path === path || c.path.startsWith(path + '/')) viewState.expanded.add(c.path); });
}
export function catCtxCollapseSub(win, viewState, path) {
  const categories = (win.DB && win.DB.categories) || [];
  categories.forEach((c) => { if (c.path === path || c.path.startsWith(path + '/')) viewState.expanded.delete(c.path); });
}
export function catCtxOnlyHere(viewState, path) {
  viewState.expanded.clear();
  const parts = path.split('/');
  for (let i = 1; i <= parts.length; i++) viewState.expanded.add(parts.slice(0, i).join('/'));
}

// "+ tag" su un fornitore del dettaglio (v1:8745-8760): prima il TAG
// confermato (se nuovo), poi il PATCH supplier — F1T2/F1T5 conservati.
export async function catAddTagToSupplier(win, supplierId) {
  const s = win.store.getSupplier(supplierId); if (!s) return false;
  const tag = (win.prompt(`Tag da aggiungere a "${s.name}":`) || '').trim();
  if (!tag) return false;
  const tags = (win.DB && win.DB.tags) || [];
  if (!tags.find((x) => x.name === tag)) {
    const rt = await win.confirmWrite.createTag(tag);
    if (!rt.ok) return false;
  }
  const r = await win.confirmWrite.supplier(s.id, { tags: [...new Set([...(s.tags || []), tag])] });
  if (!r.ok) return false;
  win.toast(`Tag "${tag}" aggiunto`, 'success');
  return true;
}

// Inline-create del picker ("+ Crea …"): quickCreate.supplier del v1
// (v1:4220-4231) è dentro una const LESSICALE non raggiungibile dal bundle —
// REPLICATO byte-per-byte sulle primitive raggiungibili (DB, confirm,
// confirmWrite.createSupplier, voltoraToast se presente). Il test statico
// pinna la sorgente v1: al primo drift diventa rosso.
export async function catQuickCreateSupplier(win, name, ctx = {}) {
  const norm = (s) => String(s || '').trim().toLowerCase();
  const n = String(name).trim(); if (!n) return null;
  const suppliers = (win.DB && win.DB.suppliers) || [];
  const dup = suppliers.find((s) => norm(s.name) === norm(n));
  if (dup && win.confirm(`Esiste già un fornitore "${dup.name || dup.path}".\n\nOK = usa quello esistente · Annulla = crea comunque "${n}"`)) {
    return { value: dup.id, label: dup.name };
  }
  const categories = [...new Set((ctx.categories || []).filter(Boolean))];
  // F1T2: creazione CONFERMATA — su errore toast rumoroso (dal helper) e nessuna voce fantasma
  const r = await win.confirmWrite.createSupplier({ name: n, categories });
  if (!r.ok) return null;
  const s = r.rec;
  if (typeof win.voltoraToast === 'function') win.voltoraToast(`Fornitore "${n}" creato${categories.length ? ' · ' + categories[0] : ''}`, 'success');
  return { value: s.id, label: s.name, sub: categories[0] || '' };
}

// Picker "aggiungi fornitore esistente" (v1:8772-8783): PATCH categories.
export async function catAssignSupplier(win, supId, path) {
  if (!supId) return false;
  const sup = win.store.getSupplier(supId);
  if (sup && !(sup.categories || []).includes(path)) {
    const r = await win.confirmWrite.supplier(supId, { categories: [...(sup.categories || []), path] });
    if (!r.ok) return false;
  }
  win.toast('Fornitore aggiunto a "' + path + '"', 'success');
  return true;
}

// Keyword manuale add/remove (v1:8886-8933): STESSA fetch (endpoint, Bearer,
// body {path, keyword, op}), stesso aggiornamento locale (selObj.keywords +
// saveStore) e stessi toast (nel componente). Ritorna il JSON del server;
// su HTTP non-ok LANCIA con la frase italiana del server (data.error) se c'e'
// (AI4, pacchetto AI05), altrimenti 'Errore <status>'; il chiamante mostra 'Errore: ' + msg.
export async function catKeywordManual(win, path, keyword, op) {
  const headers = { 'Authorization': 'Bearer ' + (win.localStorage.getItem('crm_token') || ''), 'Content-Type': 'application/json' };
  const r = await win.fetch('/api/ai/category/keyword/manual', {
    method: 'POST', headers,
    body: JSON.stringify({ path, keyword, op }),
  });
  if (!r.ok) { let m = ''; try { m = (await r.json()).error || ''; } catch (_) {} throw new Error(m || ('Errore ' + r.status)); }
  const data = await r.json();
  const selObj = ((win.DB && win.DB.categories) || []).find((c) => c.path === path);
  if (selObj) selObj.keywords = data.keywords || [];
  if (typeof win.saveStore === 'function') win.saveStore();
  return data;
}

// Genera/Rigenera keyword AI (v1:8935-8961): STESSA fetch (body {path}).
export async function catKeywordGenerate(win, path) {
  const headers = { 'Authorization': 'Bearer ' + (win.localStorage.getItem('crm_token') || ''), 'Content-Type': 'application/json' };
  let r;
  try {
    r = await win.fetch('/api/ai/category/ai-tag', {
      method: 'POST', headers,
      body: JSON.stringify({ path }),
    });
  } catch (_) {
    // server non raggiungibile: mai "Failed to fetch" all'utente
    throw new Error('Connessione al server non riuscita: controlla la rete e riprova.');
  }
  if (!r.ok) {
    let d = {}; try { d = (await r.json()) || {}; } catch (_) {}
    // codice e ora del limite d'uso: la vista spegne il pulsante fino all'ora indicata
    throw Object.assign(new Error(d.error || ('Errore del server (' + r.status + '): riprova.')), { codice: d.codice || null, riprova_alle: d.riprova_alle || null, riprova_alle_ora: d.riprova_alle_ora || null });
  }
  const data = await r.json();
  const selObj = ((win.DB && win.DB.categories) || []).find((c) => c.path === path);
  if (selObj) selObj.keywords = data.keywords || [];
  if (typeof win.saveStore === 'function') win.saveStore();
  return data;
}

// --- Deleghe al v1 (modali e navigazione RESTANO v1) -------------------------
export function catOpenProductDetail(win, productId) { win.currentProductId = productId; win.showView('product-detail'); }
export function catOpenSupplierDetail(win, supplierId) { win.currentSupplierId = supplierId; win.showView('supplier-detail'); }
export function catOpenProductModal(win, productId, presetCategory) { win.openProductModal(productId, presetCategory); }
export function catOpenSupplierModal(win, path) { win.openSupplierModal(null, { presetCategory: path }); }
export function catOpenQuotationModal(win, quotationId) { win.openQuotationDetailModal(quotationId); }
// Bottone header "Aggiungi categoria (AI)" (v1:8164-8172): stessa chiamata,
// stessa selezione + toast; il redraw passa da win.renderCategories() (che a
// flag ON è il WRAP ⇒ rientra nel v3 col preambolo, come il v1 rifà tutto).
export async function catHeaderAiAssistant(win, viewState) {
  const paths = await win.openAICategoryAssistant({ description: '', multi: false });
  if (Array.isArray(paths) && paths.length) {
    viewState.selected = paths[0];
    catEnsureExpanded(viewState.expanded, paths[0]);
    win.renderCategories();
    win.toast('Categoria selezionata: ' + paths[0], 'success');
  }
}
export function catHeaderAiImport(win) { win.openAIProductImport(); }
// "✨ Categorizza con AI" della sentinella (v1:8652-8656).
export function catAiCategorize(win, products) {
  const list = (products || []).slice();
  if (!list.length) { win.toast('Nessun prodotto da categorizzare', 'info'); return; }
  win.openAIProductImport({ mode: 'categorize', products: list });
}

// Globali/metodi delegati dalla vista Categorie (guardie install + test
// statico). confirmWrite e store sono OGGETTI su window: guardia sui metodi
// usati. NB niente 'rerenderAll' qui: la vista ridisegna se stessa e i
// rientri dai modali passano già da rerenderAll → renderCategories (wrap).
// NB quickCreate NON è su window (const lessicale, v1:4215): l'inline-create
// del picker è REPLICATO (catQuickCreateSupplier) su confirmWrite.createSupplier.
export const CAT_DELEGATED_GLOBALS = [
  'showView', 'toast', 'prettyLabel', 'saveStore',
  'openProductModal', 'openSupplierModal', 'openQuotationDetailModal',
  'openAICategoryAssistant', 'openAIProductImport', 'makeSearchableSelect',
];
export const CAT_DELEGATED_METHODS = {
  confirmWrite: ['createCategory', 'renameCategory', 'deleteCategory', 'moveCategory', 'supplier', 'createTag', 'createSupplier'],
  store: ['categoryTree', 'isLeaf', 'listSuppliers', 'listProducts', 'listQuotationsByProduct', 'getSupplier'],
};
